<template>
  <div
    v-if='streamInitialized'
    v-show="(currentSegment || !initialized)"
    :class="[
      'vi-player-'+ bulbVideo.id,
      {
        'youtube-friendly-ui': enableYoutubeFriendlyUi,
      }
    ]"
    >
    <div id="transparentYouTubeBlocker" v-if="exposeYouTubeControls"></div>
    <v-progress-linear
      v-show='buffering && desiredState !== "paused"'
      class='buffer-progress branding-primary'
      :indeterminate="buffering">
    </v-progress-linear>
    <div
      v-show="isOffline"
      class="player-offline-buffer"
    >
      <v-progress-circular
        v-show="isOffline"
        indeterminate
        :size="(!mobile) ? 70 : 40"
        width="5"
    >
      </v-progress-circular>
    </div>
    <transition :name="computedTransition">
      <div v-show="playButtonClicked || (youTubePlayClicked || exposeYouTubeControls)">
        <video v-if="activeStream.type !== 'audio'" :id="'video-embed-' + bulbVideo.id" webkit-playsinline></video>
        <!--
          This video element, is *** ONLY EVER USED*** when debugging HLS streams -- it never renders unless
          the developer wants it to.
        -->
        <video
          v-if="hasPlayerVhsLogging"
          id="hls-debugging-video"
          muted
          :class="[showHlsDebugginVideoOverlay ? 'hls-debugging-overkill-video-class' : 'd-none']">
        </video>
      </div>
    </transition>
    <audio v-if="activeStream.type == 'audio'" :id="'video-embed-' + bulbVideo.id" ></audio>
    <!-- Reference comment above -->
    <div
      v-if="hasPlayerVhsLogging"
      id="hlsStatsDisplayContainer"
      :class="[showHlsDebugginStatsContainer ? 'hls-debugging-overkill-stats-container' : 'd-none']">
      <br />
      <label>Buffer state:</label>
      <pre id="bufferedOut"></pre>
    </div>
  </div>
</template>
<script>
/* eslint-disable */

// Components:
import VideoPlayer from '@/components/mixins/VideoPlayer';
import EventBus from '@/buses/EventBus';

// Services:
import PlayerService from '../services/PlayerService';
import XApiService from '@/services/XApiService';

// Global Mixin(s):
import PlayPositOffline from '@mixins/Offline/Offline.js';
import Toast from '@mixins/Toast/Toast.js';

// Constants:
import {
  CONSOLE_LOG_STYLE_TYPES,
  MINIMUM_LOOKAROUND_SCHOOLS,
  PLAYER_VIDEO_INTEGRATION_TYPES,
} from '@Constants';

// Plugins / Libraries:
import {
  generateNumber
} from '@utilities';
import {
  mapGetters,
  mapMutations,
  mapState,
  mapActions
} from 'vuex';
import _ from 'lodash';
import moment from 'moment';
import isFastNet from 'isfastnet';

const mergeVideojsOptions = videojs.mergeOptions || videojs.util.mergeOptions;

export default {
  name: 'video-instance',
  props: [
    'bulbVideo', // actually passed from parent component
    'index', // index of the video in bulbs
  ],
  mixins: [
    PlayPositOffline,
    Toast,
    VideoPlayer,
  ],
  data() {
    return {
      bufferCounter: 0,
      buffering: false, // whether or not to show the buffering progress bar above the video, each player will need specific listeners to trigger this var
      currentAnimationTime: 0,
      liveSessionCheck: false,
      minimumLookAround: 0.025,
      pausing: false,
      player: null,
      player_fuzz_time: 0,
      playerHotkeysSeekNavigationEvent: false,
      playerHotkeysSeekNavigationTimeout: null,
      playerTime: null,
      retryPlaylist: 0,
      streamInitialized: false, // used for resetting and rebuilding the video playre div
      seeking: false,
      seekTimestamp: 0,
      selectedCaptionId: null,
      streamFails: 0,
      gateSettings: null,
      updateTime: 0, // helper var for videojs seeking
      ytSeekTime: null,
      animationFrameId: null,
      isTimeLoopActive: false,
      throttleTime: 0,
      timeUpdateDifference: 0,
      timeDifferenceInterval: null,
      ytTick: false,
      ytCheck: false,
      vimeoTick: false,
      vimeoCheck: false,
      vimeoPlayPromiseInterval: null,
      ytCaptionCollectionAttempts: 0,
      ytHasCollectedYoutubeCaptions: false,
      isVr: null, // duct to check if current (Youtube) player is vr
      internetSpeed: 'fast',
      internetLatencyData: {},
      panoptoTechDebugOption: false,
      vimeoTechDebugOption: false,
      showHlsDebugginVideoOverlay: false,
      showHlsDebugginStatsContainer: false,
      vhsLoggerEnabled: false,
      hlsLoggerEnabled: false,
      vhsDebuggingOverload: false,
      hlsDebuggingOverload: false,
      defaultHtml5HlsPlaybackTechConfigOptions: {
        vhs: {
          bandwidth: 3000000,
          limitRenditionByPlayerDimensions: false,
        },
      },
      safariHtml5HlsPlaybackTechConfigOptions: {
        nativeAudioTracks: false,
        nativeVideoTracks: false,
        vhs: {
          overrideNative: true,
        },
      },
    };
  },
  computed: {
    ...mapGetters('adaptive', [
      'adaptive_bulb',
      'bulb_complexity',
      'complexity_levels',
      'nsf_id',
      'segment_complexity',
      'treatment',
    ]),
    ...mapGetters('player', [
      'activePausingInteractions',
      'allowRewind',
      'bulb',
      'bulbAttempt',
      'bulbInitialized',
      'bulbVideos',
      'captionReset',
      'desiredState',
      'displayTime',
      'displayDuration',
      'enableYoutubeFriendlyUi',
      'exposeYouTubeControls',
      'hasNewInteractionDisplayAccess',
      'hasPlayerKeyboardShortcutsFeature',
      'hasPlayerVhsLogging',
      'hasVimeoReorderStreamsFeature',
      'hasPlayerFadeInTransitionFeature',
      'initializingBulb',
      'isPlaylist',
      'isBarbri',
      'mobileIOS',
      'lookaround',
      'manualPause',
      'maxTime',
      'minSidebarPx',
      'mobile',
      'mute',
      'playerState',
      'requiredMaxTime',
      'schoolId',
      'scrubbing',
      'sidebarOpen',
      'sidebarTab',
      'speed',
      'triggerFallbackStream',
      'user',
      'userAgent',
      'volume',
      'ytInitializing',
      'ytInitialized',
      'ytVr',
      'vimeoInitializing',
      'vimeoInitialized',
      'panoptoInitialized',
      'panoptoInitializing',
      'isBroadcast',
      'isBroadcastTeacher',
      'isBroadcastStudent',
      'termForBulb',
      'termForBulbArticle',
      'shouldUseYoutubeEducationPlayer',
    ]),
    ...mapState('player', [
      'ccOn',
      'bulbVideoQualityLevels',
      'hideCaptionsEnableTranscript',
      'intersectingCrop',
      'isLiveSession',
      'playlistResourceLinkId',
      'playlistLmsConsumerVersion',
      'formatReferer',
      'getDisplayTime',
      'playbackModeType',
      'selectedStreamQuality',
      'sessionId',
      'setFirstFocus',
      'screenfull',
      'youTubePlayClicked',
      'showedMobileIOSPanoptoInitializeToast', // strictly for additional init logic for iOS Panopto bulbs
      'playButtonClicked',
      'pqOrigin',
      'proquestVideoUrl',
      'invokeVimeoFallbackCaptionLogic',
      'ppWrapper',
      'thirdPartyCookiesEnabled',
      'youtubePlayerConfiguration',
    ]),
    ...mapGetters('interactions', [
      'activeInteraction',
      'activeInteractionGroup',
      'activeInteractionMeta',
      'interactions',
    ]),
    hasActiveRequiredInteractions() {
      if (this.user.userType === 'teacher' && this.playbackModeType === 'instructor') return false;
      return _.filter(this.activeInteractionGroup, is => is.interaction.isRequired && !is.interactionMeta.isInteractionAnswered).length > 0;
    },
    activeStream() {
      if (this.bulbVideo.bulbVideoStreams[this.bulbVideo.streamIndex]) {
        const ac = {
          player: 'videojs',
          type: this.formatType(this.bulbVideo.bulbVideoStreams[this.bulbVideo.streamIndex]), // defined in VideoPlayer mixin
          url:  this.formatUrl(this.bulbVideo.bulbVideoStreams[this.bulbVideo.streamIndex]), // defined in VideoPlayer mixin
          ...(
            (
              this.formatType(this.bulbVideo.bulbVideoStreams[this.bulbVideo.streamIndex]) === 'video/vimeo')
              && {
                view: this.bulbVideo.bulbVideoStreams[this.bulbVideo.streamIndex].view
              }
            ),
        }
        return  ac;
      }
      return this.bulbVideo.bulbVideoStreams[this.bulbVideo.streamIndex] || {player:null, type: null};
    },
    activeCaptions() {
      if (!this.bulbInitialized) return null;
      if (this.bulbVideo.captionId !== '-1') {
        return _.find(
          this.bulbVideo.bulbVideoCaptions,
            bvc => String(bvc.id) === String(this.bulbVideo.captionId)
        );
      }
      return {
        id: '-1',
        file: null
      };
    },
    initialized() {
      return this.bulbVideo.deviceInitialized && this.bulbVideo.videoInitialized;
    },
    hasPanoptoVideoPlaybackFeature() {
      return !_.get(this, 'bulb.config.panoptoHLSOverride', false);
    },
    currentSegment() {
      // video_start <= display_time < video_end
      const [
        displayTimeStartCheck,
        displayTimeEndCheck
      ] = [
        (this.bulbVideo.displayStart - this.displayTime),
        (this.bulbVideo.displayEnd - this.displayTime),
      ];
      if (this.bulbInitialized) {
        return Number(displayTimeStartCheck.toFixed(3)) <= .001 && Number(displayTimeEndCheck.toFixed(3)) >= .001;
      }
      return false;
      // OTHER CONSIDERATION:
      // If a player doesn't advance the player time to the exact end time, it woun't progress
      // to the next player, will need to bump the player time to the bulb.end_time
    },
    videoInstanceEmbeddedInIframe() {
      const isVideoInstanceEmbeddedInIframe = (
        window.location !== window.parent.location
      );
      if (!this.isPlaylist) this.setIsVideoInstanceEmbeddedInIframe(isVideoInstanceEmbeddedInIframe);
      return isVideoInstanceEmbeddedInIframe;
    },
    ytIframe() {
      return (
        window.location !== window.parent.location
        && this.activeStream.type === 'video/youtube'
        && (this.userAgent.browser.name === "Chrome" || this.userAgent.browser.name === 'Edge')
      );
    },
    vimeoIframe() {
      const isVimeoIframe = (
        window.location !== window.parent.location
        && this.activeStream.type === 'video/vimeo'
        && (this.userAgent.browser.name === "Chrome" || this.userAgent.browser.name === 'Edge')
      );
      if (!this.isPlaylist) this.setIsVimeoIframe(isVimeoIframe);
      return isVimeoIframe;
    },
    isPanoptoEmbedIframe() {
      const isPanoptoEmbeddedIframe = (
        window.location !== window.parent.location
        && this.activeStream.type === 'video/panopto'
        && (this.userAgent.browser.name === "Chrome" || this.userAgent.browser.name === 'Edge')
      );
      if (!this.isPlaylist) this.setIsPanoptoEmbeddedIframe(isPanoptoEmbeddedIframe);
      return isPanoptoEmbeddedIframe;
    },
    handleVimeoIframeEmbed() {
      // Canvas and D2L uses iframe delegation, adding the autoplay policy permission to their iframe, which is the parent 
      // iframe to the iframe that the Vimeo player sdk creates.
      if (
        this.inCanvasLMS
        || this.inD2LLMS
      ) {
        return this.VimeoInitialized();
      }
      if (
        this.activeStream.type === 'video/vimeo'
        && this.activeStream.view === 4
      ) {
        return this.VimeoInitialized();
      }
      if (
        this.currentSegment
        && this.vimeoIframe
        && !this.vimeoInitialized
        && !this.vimeoCheck
      ) {
        this.vimeoCheck = true;
        this.vimeoTick = null;
        /**
         * iframes without allow="autoplay *;" will prevent videojs vimeo embed videos from playing 
         * and don't trigger any events except for a warning at the vimeo iframe level.
         * This triggers logic to bring the Vimeo video to the top. (ㆆ _ ㆆ)
         */
        if (
          !this.vimeoTick
          && !this.vimeoInitialized
        ) {
          this.vimeoIframeInit(this.bulbVideo.id, this.currentSegment);
          this.vimeoPlayPromiseInterval = setInterval(() => {
            this.togglePlay();
          }, 1000);
          this.setSnackbar({
            text: `Please click play again.`,
            color: 'warning',
            timeout: 5000,
          });
        }
        else {
          this.vimeoCheck = false;
        }
      }
    },
    handleYouTubeIframeEmbed() {
      // Canvas and D2L uses iframe delegation, adding the autoplay policy permission to their iframe, which is the parent 
      // iframe to the iframe that the YouTube player sdk creates.
      if (
        this.inCanvasLMS
        || this.inD2LLMS
      ) {
        return this.youtubeInitialized();
      }
      if (
        this.currentSegment
        && this.ytIframe
        && !this.ytInitialized
        && !this.ytCheck
      ) {
        this.ytCheck = true;
        this.ytTick = null;
        // YOUTUBE IFRAME DUCT TAPE
        // iframes without allow="autoplay *;" will prevent videojs youtubes from playing and don't trigger any events except for a warning at the youtube iframe level
        // this triggers logic to bring that youtube video to the top
        if (!this.ytTick && !this.ytInitialized) {
          this.youtubeIframeInit(this.bulbVideo.id, this.currentSegment);
          this.player.pause();
          this.setSnackbar({
            text: 'Please click play again.',
            color: 'warning',
            timeout: 5000,
          });
        }
        else {
          this.ytCheck = false;
        }
      }
    },
    handlePanoptoEmbed() {
      if (
        this.currentSegment
        && this.isPanoptoEmbedIframe
        && !this.panoptoInitialized
        && !this.panoptoCheck
      ) {
        this.panoptoCheck = true;
        this.panoptoTick = null;
        if (
          !this.panoptoTick
          && !this.panoptoInitialized
        ) {
          this.panoptoIframeInit(this.bulbVideo.id, this.currentSegment);
          this.setSnackbar({
            text: `Please click play again.`,
            color: 'warning',
            timeout: 5000,
          });
        }
        else {
          this.panoptoCheck = false;
        }
      }
    },
    seekVariance() {
      // change playback speed changes how much MP4 and HLS player time change between each tick. As the playback speed increases, the time between playerTime updates increase
      return Math.max(2, parseInt(this.bulb.config.speed) * .3);
    },
    /**
     * Source of truth to determine if the bulb is in a Canvas LMS environment:
     * 
     * - bulbAttempt resourceLinkId
     * - user lms consumer version
     *    - Usually `canvas-cloud`
     * - ancestor origins
     *    - Every institution (or school) uses a specific URL to access Canvas. Institutions follow a URL structure of
     *      [your institution name].instructure.com
     *      or
     *      canvas.[your institution name].edu
     * 
     * To be honest -- the code below looks gross and isn't that readable, will refactor later when we have time.
     */
    inCanvasLMS() {
      return (
        (
          (this.isPlaylist)
          ? (
            this.playlistResourceLinkId?.includes('canvas')
            || this.playlistLmsConsumerVersion.includes('canvas')
          )
          : this.bulbAttempt?.resourceLinkId?.includes('canvas')
        )
        || this.user?.lmsConsumerVersion?.includes('canvas')
        || this.checkIfAncestorOriginContainsCanvas()
      );
    },
    /**
     * Source of truth to determine if the bulb is in a D2L LMS environment:
     * 
     * - bulbAttempt resourceLinkId
     * - user lms consumer version
     *    - Usually `desire2learn`
     * - ancestor origins
     * 
     * To be honest -- the code below looks gross and isn't that readable, will refactor later when we have time.
     */
    inD2LLMS() {
      return (
        (
          (this.isPlaylist)
          ? (
            this.playlistResourceLinkId?.includes('d2l')
            || this.playlistLmsConsumerVersion.includes('desire2learn')
          )
          : this.bulbAttempt?.resourceLinkId?.includes('d2l')
        )
        || (
          this.user?.lmsConsumerVersion?.includes('desire2learn')
          && !this.bulbAttempt?.resourceLinkId?.includes('nileelt') // Nile uses a custome D2L implementation that *doesn't* allow event delegation
        )
        || this.checkIfAncestorOriginContainsD2l()
      );
    },
    useDefaultDuration() {
      /**
       * Whether to use the default duration stored in the database or wait for the video's metadata to return the duration
       */
      // if (this.activeStream.type === 'video/youtube') {
      //   // DUCKTAPE YOUTUBE - youtude's duration will not display until the video starts playing
      //   // as long as the player duration is 0 we want to use the database for the duration
      //   return this.player.duration() === 0;
      // }
      if (this.activeStream.type === 'video/panopto') return isNaN(this.player.duration());
      if (
        this.activeStream.type === 'video/vimeo'
        && this.activeStream.view === 4
      ) {
        return this.player.duration() === 0;
      }
      return false;
    },
    // RELATED TO PLAYBACK REWORK
    gates() {
      return {
        play: [
          {
            key: 'broadcastStudentCheck',
            result: (props) => {
              return this.isBroadcast && this.user.userType === 'student' ? false : true;
            }
          },
          {
            key: 'notCurrentSegment',
            result: (props) => {
              // If this video instance is the current segment OR the entire bulb has hasn't been initialized, return true
              return this.currentSegment || !this.initialized;
            }
          },
          {
            key: 'videoComplete',
            result: (props) => {
              // If not at the end of the video, return true
              return this.displayTime < this.displayDuration;
            }
          },
          {
            key: 'requiredQuestions',
            result: (props) => {
              return (!this.hasActiveRequiredInteractions || this.displayTime < this.requiredMaxTime - .200);
            },
            failureAction: (props) => {
              const activeByDisplayOutTime = _.orderBy(this.activeInteractionGroup, ['interaction.displayOutTime'])
              const req = _.find(activeByDisplayOutTime, is => is.interaction.isRequired && !is.interactionMeta.isInteractionAnswered);
              if (req) {
                if (!req.interactionMeta.overlay && (!this.activeInteraction || req.interaction.id !== this.activeInteraction.id)) {
                  this.requestSetActiveInteraction(req.interaction);
                }
                else {
                  if (this.activeInteraction && req.interaction.id === this.activeInteraction.id) this.openInteractionsTab();
                  this.$nextTick(() => {
                    document.querySelector(`#assist-${req.interaction.id}`).focus();
                  });
                }
              } else {
                this.requestSetActiveInteraction('next')
              }
              if (!this.isLiveSession) {
                this.setSnackbar({
      		        text: `Please complete the required interaction to continue.`,
      		        timeout: 6000,
      		      })
              }
            }
          },
        ],
        pause: [
          {
            key: 'notInitialized',
            result: (props) => {
              return this.initialized;
            }
          },
          {
            key: 'alreadyPausing',
            result: (props) => {
              return !this.pausing;
            }
          }
        ],
        seek: [
          {
            key: 'noRewinding',
            result: (props) => {
              // If the requested seek time is between the start time and the maximum time, return true
              if (this.allowRewind) return true
              // get props.seek time as display_time
              const seek_time = _.has(props, 'seekPlayerTime') ? this.getPlayerTime(props.seekPlayerTime) : props.seekTime;
              // new seek time must be greater than the display
              return this.displayTime <= seek_time
            }
          },
          {
            key: 'notWithinDisplayDuration',
            result: (props) => {
              // the give display seek is between 0 and the bulb's duration
              if (_.has(props, 'seekTime')) {
                return 0 <= props.seekTime && props.seekTime <= this.displayDuration;
              }
              return _.has(props, 'seekPlayerTime');
            }
          },
          {
            key: 'notWithinVideoDisplayBounds',
            result: (props) => {
              // the given display seek is between
              if (_.has(props, 'seekTime')) {
                return (
                  this.bulbVideo.displayStart <= props.seekTime
                  && props.seekTime <= this.bulbVideo.displayEnd
                );
              }
              return _.has(props, 'seekPlayerTime');
            }
          },
          {
            key: 'notWithinPlayerBounds',
            result: (props) => {
              // If the requested seek time is between the start time and the maximum time, return true
              if (_.has(props, 'seekPlayerTime')) {
                return (
                  this.bulbVideo.playerStartTime <= props.seekPlayerTime &&
                  props.seekPlayerTime <= this.bulbVideo.playerEndTime &&
                  props.video_id === this.bulbVideo.id
                )
              }
              return true;
            }
          },
          {
            key: 'cannotPassMaxTime',
            result: props => {
              // if fastforward is disabled prevent seeking past max time
              if (this.bulb.config.fast_yes) {
                // bulb allows fast forwarding
                return true;
              }
              if (_.has(props, 'seekPlayerTime')) {
                /**
                 * For bulbs that use the new Vimeo and Panopto embed flow:
                 * > They don't fire the timeupdate playback event as consistently as the other playback techs do, 
                 * such as the HTML5 tech. The consequence of this, is that the maxTime we set for the bulbAttempt is 
                 * somewhat 'behind', the actual displayTime for the bulb. To account for this discrepancy, we need to
                 * add a greater seek buffer variance to the final check below -- if we don't do
                 * this, we end up returning false for an internal crop which makes our associated crop logic error out, resulting
                 * in the player not jumping to crop start time.
                 */
                const seekBufferVariance = (this.activeStream.type === 'video/vimeo' || this.activeStream.type === 'video/panopto' ) ? .5 : .1;
                // convert playerTime to display time then check that it is before the maxTime
                return this.getDisplayTime(this.bulbVideo.id, props.seekPlayerTime) <= (this.maxTime + seekBufferVariance);
              } else {
                // make sure seekTime is less than maxTime
                return props.seekTime <= this.maxTime;
              }
            }
          },
          {
            key: 'cannotPassRequiredMaxTime',
            result: (props) => {
              // If the requested seek time is between the start time and the maximum time, return true
              let time;
              if (_.has(props, 'seekPlayerTime')) {
                time = this.getDisplayTime(this.bulbVideo.id, props.seekPlayerTime);
              } else {
                time = props.seekTime;
              }
              return time <= this.requiredMaxTime;
            },
            failureAction: () => {
              // console.log('Seeking to required max time!', this.requiredMaxTime);
              // [ ] DUCTTAPE youtube auto plays after this seek, seek a little bit ahead so the other watchers stop it from playing
              EventBus.$emit('requestAction', {key: 'seek', seekTime: this.requiredMaxTime - .001, preventSyncState: true});
            }
          },
        ],
        // Maybe updating time...?
        // Maybe changing the active question...?
      }
    },
    requestAction() {
      return {
        play: (props) => {
          /**
           * Identified that the Panopto embed splash overlay disappears when the embed API is used to 
           * programmatically request playback even if the root frame (Moodle in this instance) does not support
           * autoplay, i.e doesn't use iframe-delegation. Unlike other embed APIs we've worked with, the Panopto splash overlay is the 
           * only way for a user to trigger a user click to unlock the video and since it disappears on failure of 
           * programmtic play, it locks the user into an environment where the Panopto player cannot play. 
           * 
           * Once again, this is a policy uniquely implemented in Chrome 66+ and other browsers are less restrictive and don't
           * have an autoplay policy set in place.
           * 
           * More information could be found regarding Chromes' auto play policy and the resoning behind it here:
           * https://developer.chrome.com/blog/autoplay/
           * 
           */
          if (
            this.currentBulbVideoStreamTypeIsPanopto
            && this.isPanoptoEmbedIframe
            && !this.panoptoInitialized
            && (
              !this.isPlaylist
              || this.framedInExternalEnvironment.value
            )
          ) {
            this.handlePanoptoEmbed;
            return;
          }

          if (props.setDesiredState) {
            this.setDesiredState('play');
          }
          let allowed = this.isActionAllowed(props);
          if (allowed.result) {
            this.setManualPause(false);
            XApiService.buildPlayVideoStatement({
              activity: {
                id: `https://xapi.playposit.com/activities/video/${this.bulbVideo.id}`,
                name: this.bulb.title,
                lesson_id: this.bulb.id
              }
            });
            this.gateSettings.play();
            this.setPlayerState('play');
          } else {
            // if (this.currentSegment) console.log(this.bulbVideo.id, 'CANNOT PLAY DUE TO ', allowed);
            if (allowed.failureAction) {
              allowed.failureAction(props);
            }
          }
        },
        pause: (props) => {
          if (props.setDesiredState) {
            this.setDesiredState('pause');
          }
          let allowed = this.isActionAllowed(props);
          // console.log(allowed)
          if (allowed.result) {
            this.setManualPlay(false);
            XApiService.buildPauseVideoStatement({
              activity: {
                id: `https://xapi.playposit.com/activities/video/${this.bulbVideo.id}`,
                name: this.bulb.title,
                lesson_id: this.bulb.id
              }
            });
            this.gateSettings.pause();
            this.setPlayerState('paused');
          } else {
            // console.log('CANNOT PAUSE DUE TO ', allowed.key);
            if (allowed.failureAction) {
              allowed.failureAction(props);
            }
          }
        },
        seek: (props) => {
          let allowed = this.isActionAllowed(props);
          if (allowed.result) {
            this.seekTimestamp = moment();
            this.seeking = true;
            if (_.has(props, 'seekTime')) {
                this.updateTime = this.getPlayerTime(props.seekTime);
                this.gateSettings.seek(this.updateTime);
                this.setDisplayTime(props.seekTime)
                if (!props.preventSyncState && props.seekTime !== this.requiredMaxTime) {
                  this.$nextTick(() => {
                    this.triggerSyncState();
                  })
                }
                if (this.playerHotkeysSeekNavigationEvent) {
                  EventBus.$emit('handlePlayerOverlaySeekStep', {
                    arrowType: this.arrowType,
                    seekStep: this.seekStep,
                  });
                }
            } else {
                this.updateTime = props.seekPlayerTime;
                this.gateSettings.seek(this.updateTime);
                if (!props.preventSyncState && this.getDisplayTime(props.seekPlayerTime) !== this.requiredMaxTime) {
                  this.$nextTick(() => {
                    this.triggerSyncState();
                  })
                }
            }
          } else {
            // if (this.currentSegment) console.log(this.bulbVideo.id, 'CANNOT SEEK DUE TO ', allowed);
            if (allowed.failureAction) {
              allowed.failureAction(props);
            }
            const { key } = allowed;
            if (
              (
                key === 'noRewinding'
                || key === 'cannotPassRequiredMaxTime'
              )
              && this.currentSegment
            ) {
              this.setSnackbar({
                text: 'Complete current interaction before seeking.',
                color: 'warning',
              });
              const preventingRewind = _.find(this.activeInteractionGroup, aig => !aig.interaction.rewind && !aig.interactionMeta.isInteractionAnswered && !aig.interactionMeta.hiddenByRequired);
              if (preventingRewind) {
                if (
                  this.activeInteraction
                  && this.activeInteraction.id === preventingRewind.interaction.id
                  && (
                    !this.showInteractionsTab
                    || !this.sidebarOpen
                  )
                ) {
                  this.openInteractionsTab();
                  document.querySelector(`#assist-${preventingRewind.interaction.id}`).focus();
                }
                else if (!preventingRewind.interactionMeta.overlay) {
                  this.requestSetActiveInteraction(preventingRewind.interaction);
                }
                else {
                  this.$store.getters['interactions/interactionMeta'](preventingRewind.interaction.id).collapsed = false;
                  this.$nextTick(() => {
                    document.querySelector('.interaction.noRewind:not([style*="display: none"]) .assistive-status').focus();
                  });
                }
              }
            }
            if (key === 'cannotPassMaxTime') {
               this.setSnackbar({
                text: 'Fast Forwarding is disabled.',
                color: 'warning',
              });
            }
          }
        },
      };
    },
    throttledTimeUpdateLoop() {
      return _.throttle(this.timeUpdateLoop, this.throttleTime);
    },
    framedInExternalEnvironment() {
      /**
       * We are using the read-only `ancestorOrigins` property on the Location interface to determine whenever the 
       * document is being framed (potentially nested) by a site which we don’t expect it to be framed by. This is useful
       * because we could determine if we are being framed from an external site / env. other than our own.
       * 
       * Additionally, we can also use it to vary the behavior of the document based on what site or list of sites is framing it
       * which we want to do for bulbs that are in a playlist, that are embedded *outside* of our environment because that
       * will determine whether or not we need to invoke our extra initialization logic for frames that don't have the
       * autoplay permission policy set in Chrome on the *parent* iframe.
       * 
       * 
       * Notice the differences in the DOMStringList object depending on what environment we are in:
       * 
       * ~ Within our PlayPosit environment~
       * DOMStringList:
       * {
       * "0": "https://api.playposit.com", <-- the first entry, will *always* be our origin.
       * length: 1
       * }
       * 
       * ~ Within Canvas's environment (although, we don't have to worry about Canvas LMS because we've identified that they *always* set the autoplay permission policy at the root parent iframe):
       * DOMStringList:
       * {
       * "0": "https://apityrus.playpauseit.com",
       * "1": "https://playpostit.instructure.com" <-- reference to Canvas's origin that was set for the browsing context
       * length: 2,
       * }
       * 
       * ~ Within Blackboards' environment (we've identified that the autoplay permission policy at the root parent iframe is *not* set):
       * DOMStringList:
       * {
       * "0": "https://api-dev.playposit.com",
       * "1": "https://bd-partner-a-original.blackboard.com" <-- reference to Blackboard's origin for the browsing context
       * length: 2,
       * }
       */
      return {
        value: (window?.location?.ancestorOrigins?.length > 1),
        ancestorOrigins: window?.location?.ancestorOrigins,
      }
    },
    computedTransition() {
      const [
        lockdownCondition,
        vimeoEmbedPlayerCondition,
        vimeoEmbedPlayerInteractionZerothCondition,
        panoptoEmbedPlayerCondition,
      ] = [
        (
          this.hasVimeoReorderStreamsFeature
          && this.bulbVideo.playerStartTime !== 0
          && this.activeStream.type === 'application/x-mpegURL'
          && this?.interactions[0]?.displayInTime === 0
          && [1755, 12].includes(this.schoolId) // for now, just enable this feature for LBS and our test demo school.
        ),
        (
          this.activeStream.type === 'video/vimeo'
          && this.bulbVideo.playerStartTime !== 0
        ),
        (
          this.activeStream.type === 'video/vimeo'
          && this?.interactions[0]?.displayInTime === 0
        ),
        (
          this.activeStream.type === 'video/panopto'
          && this.bulbVideo.playerStartTime !== 0
        ),
      ];
      if (
        lockdownCondition
        || vimeoEmbedPlayerCondition
        || panoptoEmbedPlayerCondition
        || this.hasPlayerFadeInTransitionFeature
      ) {
        return 'fade';
      }
      if (vimeoEmbedPlayerInteractionZerothCondition) return 'vimeo-zeroth-interaction-fade';
      return 'none';
    },
    /**
     * Specific player events for our videojs player Hotkeys plugin.
     */
    playerHotkeysListeners() {
      return {
        'hotkeys:mute': (event, data) => {
          this.setMute(data.mute);
          this.setVolume((data.mute) ? 0 : 40);
          this.$set(this.bulb.config, 'volume', (data.mute ? 0 : 40));
          EventBus.$emit('handleMuteAlertIcon', {
            muted: data.mute,
        });
      },
      'hotkeys:volumeDown': (event, data) => {
        this.setVolume(data.volume);
        this.$set(this.bulb.config, 'volume', data.volume);
        if (data.volume < 0) return;
        EventBus.$emit('handleVolumeDownIcon', {
          volume: data.volume,
        });
      },
      'hotkeys:volumeUp': (event, data) => {
        this.setVolume(data.volume);
        this.$set(this.bulb.config, 'volume', data.volume);
        if (data.volume > 100) return;
        EventBus.$emit('handleVolumeUpIcon', {
          volume: data.volume,
        });
      },
      'hotkeys:seekNavigation': (event, data) => {
        const { arrowType, seekStep } = data;
        this.arrowType = arrowType;
        this.seekStep = seekStep;
        this.playerHotkeysSeekNavigationEvent = true;
        clearTimeout(this.playerHotkeysSeekNavigationTimeout);
        this.playerHotkeysSeekNavigationTimeout = setTimeout( () => {
          this.playerHotkeysSeekNavigationEvent = false;
        }, 500);
      },
      'hotkeys:toggle-sidezone': () => {
        EventBus.$emit('toggle-player-sidezone');
      },
      'hotkeys:toggle-settings-menu': () => {
        EventBus.$emit('toggle-player-settings-menu');
        },
      };
    },
    currentBulbVideoStreamTypeIsPanopto () {
      return this.bulbVideos[this.index].bulbVideoStreams[0].type === 'panopto';
    },
    offlineEventListeners() {
      return {
        'up': () => {
          this.isOffline = false;
          EventBus.$emit('requestAction', { key: 'play', setDesiredState: true });
        },
        'down': () => {
          this.isOffline = true;
          EventBus.$emit('requestAction', { key: 'pause' });
        },
      }
    },
  },
  methods: {
    ...mapMutations('adaptive', [
      'setBulbComplexity',
      'setNsfId',
      'setSegmentComplexity',
    ]),
    ...mapMutations('player', [
      'openInteractionsTab',
      'setBulbAttempt',
      'setBulbHasCaptions',
      'setBulbVideoQualityLevels',
      'setCaptionReset',
      'setCcOn',
      'setCurrentSegmentSeek',
      'setDisplayTimeStart',
      'setDesiredState',
      'setDisplayTime',
      'setInitializingBulb',
      'setLookaround',
      'setManualJump',
      'setManualPause',
      'setManualPlay',
      'setPlayerState',
      'setSessionClicked',
      'setShowHotspots',
      'setSidebarOpen',
      'setSnackbar',
      'setYoutubeActiveStream',
      'youtubeIframeInit',
      'vimeoIframeInit',
      'panoptoIframeInit',
      'youtubeInitialized',
      'VimeoInitialized',
      'PanoptoInitialized',
      'setIsVimeoIframe',
      'setIsPanoptoEmbeddedIframe',
      'setIsVideoInstanceEmbeddedInIframe',
      'setPanoptoInitializing',
      'setShowedMobileIOSPanoptoInitializeToast', // strictly for additional init logic for iOS Panopto bulbs
      'setYouTubePlayClicked',
      'setYoutubeVr',
      'setPanoptoEmbedInitializationError',
      'setVimeoEmbedPlayerCaptions',
      'toggleCC',
      'setInvokeVimeoFallbackCaptionLogic',
    ]),
    ...mapActions('interactions', [
      'requestSetActiveInteraction',
    ]),
    ...mapMutations('interactions', [
			'setInteractionStates',
		]),
    ...mapActions('player', [
      'checkYoutubeBlocked',
      'updateBulbVideoDuration',
      'togglePlay',
    ]),
    checkYouTubePlay() {
      // Only run on first bulbVideo - compare videoSegment to bulbVideos array
      if (
        ![1300, 2088].includes(this.schoolId)
        || this.activeStream.type !== 'video/youtube'
        || this.youTubePlayClicked
      ) return;
      this.setYouTubePlayClicked(true);
    },
    fallbackStream(logType) {
      // for whatever reason the current activeStream failed to load, try the next available stream or redirect to blueScreen
      this.retryPlaylist = 0;
      if (this.bulbVideo.bulbVideoStreams.length === 0) {
        if (logType === 'youtube') {
          PlayerService.apiLogger(`youtube no fallback bulbVideoStreams, bulb ${this.bulb.id} section ${this.bulbAttempt.sectionId} student ${this.bulbAttempt.studentId} school ${this.schoolId}` )
        }
        this.setSnackbar({
          action: 'stream_help',
          bluescreen: {
            title: 'Missing Streams',
            subtitle: `There are no available streams for ${this.termForBulbArticle} ${this.termForBulb} segment.`,
            text: `Please click <a target="_blank" rel="noopener noreferrer" href="https://knowledge.playposit.com/article/216-troubleshooting-bulb-playback#generic">here</a> for troubleshooting directions. If these do not resolve the issue, reach out to help@playposit.org and include the numbers located in the top right of this screen.`,
            location: `VideoInstance.fallbackStream: bulbVideo ${this.bulbVideo.id}`,
          },
          color: 'error',
          text: `Video segment missing streams.`,
          timeout: 0,
        });
        return;
      }

      let nextStream = this.bulbVideo.streamIndex + 1
      if (nextStream === this.bulbVideo.bulbVideoStreams.length) nextStream = 0;
      this.streamFails += 1;
      if (this.bulbVideo.bulbVideoStreams.length === 1 || (this.streamFails === this.bulbVideo.bulbVideoStreams.length + 2)) {
        // there is only one stream or we have cycled through all the available options
        setTimeout(() => {
          if (logType === 'youtube') {
            PlayerService.apiLogger(`youtube all fallback streams failed, bulb ${this.bulb.id} section ${this.bulbAttempt.sectionId} student ${this.bulbAttempt.studentId} school ${this.schoolId}` )
          }
          const error = this.player?.error();
          PlayerService.apiLogger(JSON.stringify({
            errorType: 'failedStreams',
            errorCode: error?.code,
            errorMessage: error?.message,
            bulbId: this.bulb.id,
            sectionId: this.bulbAttempt.sectionId,
            studentId: this.bulbAttempt.studentId,
            schoolId: this.schoolId,
            streamUrls: this.bulbVideo.bulbVideoStreams.map((stream) => stream.url),
            activeStreamType: this.activeStream.type,
          }));
          this.setSnackbar({
            action: 'stream_help',
            bluescreen: {
              title: 'Failed Streams',
              subtitle: `All streams for this ${this.termForBulb} segment failed to load.`,
              code: '511',
              text: `Please click <a target="_blank" rel="noopener noreferrer" href="https://knowledge.playposit.com/article/216-troubleshooting-bulb-playback#generic">here</a> for troubleshooting directions. If these do not resolve the issue, reach out to help@playposit.org and include the numbers located in the top right of this screen.`,
            },
            color: 'error',
            text: `Video streams failed to load.`,
            timeout: 0,
          });
        }, 10)
      } else {
        // there are more streams to keep checking
        if (logType === 'youtube') {
          PlayerService.apiLogger(`youtube Fallback Stream triggered, bulb ${this.bulb.id} section ${this.bulbAttempt.sectionId} student ${this.bulbAttempt.studentId} school ${this.schoolId}` )
        }
        this.setSnackbar({
           color: 'warning',
           text: `${this.bulbVideo.bulbVideoStreams[this.bulbVideo.streamIndex].name} took longer than normal to load, switching stream.`,
           timeout: 5000,
        });
        this.bulbVideo.streamIndex = nextStream;
      }
    },
    reloadCaptions() {
      // preset captions are set in store/player.js:setBulb based on teacher.userInfo.captions_yes property
      // reload load captions that the user already selected
      _.filter(this.bulbVideo.bulbVideoCaptions, {'vjsLoaded': true}).forEach(caption => {
        this.loadVtt(caption)
      })
    },
    initializePlayerTechOrder() {
      // default player tech order:
      const playerTech = [
        'html5',
        'youtube',
        'Vimeo',
        (this.hasPanoptoVideoPlaybackFeature && this.activeStream.type === 'video/panopto') ? 'Panopto' : null,
        this.bulb?.teachers?.[0]?.school?.permissionKeys?.includes('enableIframeVideojsTech') ? 'Iframe' : null,
      ];
      return _.compact(playerTech);
    },
    initializePlayerHtml5PlaybackTechConfigurationOptions() {
      // Right now, we only have our QA ngs testing the new Html5 playback on Safari.
      // If all looks good, we will incrementally roll this out to our clients.
      if (
        (
          [
            1512582, // Ti
            1512250, // Melanie
            1582994, // Victoria
            1365817, // Luke
          ].includes(this.user.id)
          && videojs.browser.IS_SAFARI
        ) || this.$inDevEnvironment()
      ) {
        return mergeVideojsOptions(
          this.defaultHtml5HlsPlaybackTechConfigOptions,
          this.safariHtml5HlsPlaybackTechConfigOptions
        );
      } else {
        return this.defaultHtml5HlsPlaybackTechConfigOptions;
      }
    },
    initializePlayerVimeoPlaybackTechConfigurationOptions() {
      return {
        debug: this.vimeoTechDebugOption,
        debugPrefix: 'Player::VideoInstance::VimeoTech',
        playerStartTime: this.bulbVideo.playerStartTime,
        autopause: false,
        fallbackStreamLogic: this.fallbackStream,
        service: 'Player',
        setSnackbar: this.setSnackbar,
        vm: this,
      };
    },
    initializePlayerPanoptoPlaybackTechConfigurationOptions() {
      return {
        panoptoBaseUrl: _.get(this, 'bulb.teachers[0].school.panopto', null),
        onPanoptoLoginShown: this.handlePanoptoLoginShown,
        service: 'Player',
        debug: this.panoptoTechDebugOption,
        debugPrefix: 'Player::VideoInstance::PanoptoTech',
        playerStartTime: this.bulbVideo.playerStartTime,
        vm: this,
      };
    },
    initializePlayerYouTubePlaybackTechConfigurationOptions() {
      return {
        ...this.shouldUseYoutubeEducationPlayer && {
          embedConfig: {
            enc: this.youtubePlayerConfiguration,
          },
          host: 'https://www.youtubeeducation.com',
        },
        'customVars': {
          'playsinline': '1',
          'enablejsapi': '1',
          'stretch': '16:9',
          'modestbranding': '1',
          'iv_load_policy': '3',
          'cc_load_policy': 0,
          'rel': 0
          },
      };
    },
    initializePlayerIframePlaybackTechConfigurationOptions() {
      return {
        debug: false,
        pqOrigin: this.pqOrigin,
        proquestVideoUrl: this.proquestVideoUrl,
        ppWrapper: this.ppWrapper,
      };
    },
    loadStreamQualities() {
      const qualityLevels = this.player.qualityLevels();
      this.setBulbVideoQualityLevels({
        bulbVideoIndex: this.index,
        videoStreamIndex: this.bulbVideo.streamIndex,
        qualityLevels: this.generateStreamQualityLevels(qualityLevels.levels_),
      })
    },
    generateStreamQualityLevels(qualityLevels) {
      const finalQualityLevels = [];
      for(const quality of qualityLevels) {
        if (quality.height) finalQualityLevels.push(quality);
        else {
          const value = quality.id || quality.label;
          if (!value) break;
          const streamQuality = +[...new Set(value?.split('/')?.filter(item => item[item.length - 1] === 'p')?.map(item => item?.slice(0,-1)))][0];
          if (_.isFinite(streamQuality)) {
            finalQualityLevels.push({
              bitrate: quality.bitrate,
              height: streamQuality,
              id: quality.id,
              label: quality.label
            });
          }
        }
      }
      return _.orderBy(finalQualityLevels, ['height'], ['desc']);
    },
    async loadVtt(captionSet) {
      /**
      * Load vtt files
      */
      // If captions are disabled but transcripts enabled prevent text track from being added
      if (
        _.get(this.bulb, 'config.enable_transcript', '') === 1 
        && _.get(this.bulb, 'config.captions_disabled', 0) === 1
      ) return;
      if (captionSet.type === 'proquest') {
        /*
          Proquest videos can contain start/end crops WITHIN the Proquest environment (i.e. not added through the designer). To make sure that captions still align, we pass in `offsetTime` to the proxying endpoint which moves all of the captions forward or backwards by the chosen amount.
        */
        const { data: captionText } = await PlayerService.getCaptions({
          captionUrl: captionSet.ccUrl,
          type: captionSet.type,
          teacherId: this.user.id,
          offsetTime: captionSet.offsetTime,
        });
        captionSet.text = captionText;
      }

      const trackEl = this.player.addRemoteTextTrack({
        kind: 'captions',
        src: URL.createObjectURL(new Blob([captionSet.text])),
        default: 'showing',
        label: captionSet.label,
        id: captionSet.id,
      }, false); // false prevents a deprecation warning
      trackEl.addEventListener('load', () => {
        // the object has a reference to the TextTrack object via the `track` property
        captionSet.vjsLoaded = true;
        this.setCaptions(this.bulbVideo.captionId);
      });
    },
    loadMediaAmpStreams(videoStreams) {
      const maPromises = [];
      videoStreams.forEach((vs) => {
        if (vs.type === 'mediaamp' && !vs.mediaAmpLoaded) {
          maPromises.push(this.getMediaAmpUrl(vs))
        }
      });
      return Promise.all(maPromises)
    },
    getMediaAmpUrl(videoStream) {
      return new Promise((resolve, reject) => {
        PlayerService.getMediaAmpUrl(videoStream.url)
        .then(stream => {
          videoStream.mediaAmpLoaded = true;
          videoStream.url = stream.request.responseURL;
        })
        .catch(err => reject(err));
      });
    },
    initializeHlsQuality() {
      this.loadStreamQualities();
      // 228 - WP Carey is only school with streamSelector turned on
      if (this.activeStream.type === 'application/x-mpegURL' && [228].includes(_.get(this, 'bulb.teachers[0].school.id', null))) {
        // set up stream picker for hls
        const qualityLevels = this.player.qualityLevels();
        window.qualityLevels = qualityLevels
        
        //find & enable only highest quality stream on click of play thumbnail initially
        let highestQualityIndex = 0;
        let highestQualityHeight = 0;
        for (var i = 0; i < qualityLevels.length; i++) {
          let qualityLevel = qualityLevels[i];
          if (qualityLevel.height > highestQualityHeight) {
            highestQualityHeight = qualityLevel.height
            highestQualityIndex = i
          }
        }
        for (var i = 0; i < qualityLevels.length; i++) {
          let qualityLevel = qualityLevels[i];
          if (i === highestQualityIndex) {
            qualityLevel.enabled = true;
          } else {
            qualityLevel.enabled = false;
          }
        }
      }
    },
    isActionAllowed(props) {
      if (!this.gateSettings) {
        return {
          result: false,
          key: 'noGateSettings'
        }
      }
      // Loop through all gates
      for (let k in this.gates[props.key]) {
        // If a gate is included in the bypassGates array, bypass it
        if (props.bypassGates.includes(this.gates[props.key][k].key) || props.forceToAll) continue;
        // If the requested action doesn't pass a specific gate, return a failure and stop the entire process
        if (!this.gates[props.key][k].result(props)) {
          return {
            result: false,
            failureAction: this.gates[props.key][k].failureAction,
            key: this.gates[props.key][k].key
          };
        }
        // If a gate has an action to perform whenever it returns successfully, fire it off now
        if (this.gates[props.key][k].successAction) {
          this.gates[props.key][k].successAction(props);
        }
      }
      // If all gates are passed, return a success
      return {
        result: true
      };
    },
    setInitialCaption() {
      if (this.bulbVideo.bulbVideoCaptions.length) {
        // Filter for user uploaded captions
        const uploadedCaptions = _.filter(this.bulbVideo.bulbVideoCaptions, (bvc) => {
          return (
              bvc.type !== 'youtube'
              && !_.get(bvc, 'isFromIntegration', false)
            )
          
        })
        // if there are uploadedCaptions - try for English first
        if (uploadedCaptions.length) {
          const english = _.find(uploadedCaptions, c => c.label.toLowerCase() === 'english');
          this.selectedCaptionId = english ? english.id : uploadedCaptions[0].id;
        } else {
          // No uploaded captions - try for English first
          const english = _.find(this.bulbVideo.bulbVideoCaptions, c => c.label.toLowerCase() === 'english');
          this.selectedCaptionId = english ? english.id : this.bulbVideo.bulbVideoCaptions[0].id;
        }
      } else {
        // no captions
        this.selectedCaptionId = -1;
      }
    },
    setStreamQuality() {
      if (this.activeStream.type !== 'application/x-mpegURL') return;
      if (!this.player) return;
      const selectedStreamHeight = this.selectedStreamQuality[this.index].quality;
      const qualityLevels = this.player.qualityLevels();
      if (selectedStreamHeight === 'auto') {
        for (var i = 0; i < qualityLevels.length; i++) {
            qualityLevels[i].enabled = true;
          }
      } else {
        for (var i = 0; i < qualityLevels.length; i++) {
          let qualityLevel = qualityLevels[i];
          if (qualityLevel.height === selectedStreamHeight) {
            qualityLevel.enabled = true;
          } else {
            qualityLevel.enabled = false;
          }
        }
      }
    },
    setupGateSettings(playerType) {
      if (playerType === 'videojs') {
        // define methods for videos
        this.gateSettings = {
          isVideoPaused: () => {
            return this.player.paused();
          },
          isVideoBuffering: () => {
            return this.player.readyState() !== 4;
          },
          isVideoSeeking: () => {
            return this.player.seeking();
          },
          play: () => {
            this.pausing = false;
            this.player.play().then(d => {
              if (
                !this.isPlaylist
                || this.framedInExternalEnvironment.value
              ) {
                this.handleVimeoIframeEmbed;
                this.handleYouTubeIframeEmbed;
              }
            }).catch((e) => {
              const errString = e.toString();
              console.log('play err', errString)
              this.setPlayerState('paused');
              if (errString.includes('NotAllowedError') || errString.includes('abort') ) {
                this.setSnackbar({
                  text: 'Autoplay is disabled on this browser.',
                  color: 'warning',
                });
              }
            });
          },
          pause: () => {
            if (!this.paused()) {
              this.stopTimeUpdateLoop();
              this.pausing = true;
              this.player.pause();
            }
          },
          seek: (seekPlayerTime) => {
            this.resetYtCheck();
            this.resetVimeoCheck();
            this.resetPanoptoCheck();
            this.player.currentTime(seekPlayerTime);
            this.startTimeUpdateLoop();
          }
        };
      } else if (playerType === 'iframe') {
        // Settings for iframe
      }
    },
    /**
      * Sets a caption or description track from the current video to active
      * @param id: integer/string - the caption_id from DB of the text track to use. If the caption id doesn't match it is turned off;
      */
    setCaptions(id) {
      if (!this?.player) return;

      this.handleSettingYouTubePlayerCaptions(id);
      this.handleSettingVimeoPlayerCaptions(id);

      // Hide all other tracks
      const tracks = this.player.textTracks();
      for (let t = 0; t < tracks.length; t += 1) {
        // this might lead to a case where two sets of captions are set to showing if the order is new track > old track
        if (String(tracks[t].id).toLowerCase() === String(id).toLowerCase()) {
          this.player.textTracks()[t].mode = 'showing';
        } else {
          this.player.textTracks()[t].mode = 'disabled';
        }
      }
    },
    setupMediaSessionEvents() {
      /*
      * These are used to capture the play/pause events from keyboard buttons and not let them change playback
      */
      navigator.mediaSession.setActionHandler('play', function() {});
      navigator.mediaSession.setActionHandler('pause', function() {});
    },
    setMinimumLookaround() {
      if (MINIMUM_LOOKAROUND_SCHOOLS.includes(this.bulb.id)) {
        this.minimumLookAround = 0.25; 
      }
    },
    setSpeed(speed) {
      this.player.playbackRate(parseFloat(speed));
    },
    setVolume(vol){
      /**
      * Set the volume of the video player
      * @param vol: interger between 0 - 100 to set the new volume level
      */
      // [ ] vuex to store and get the state.volume settings
      // iOS prevents changing audio with api or reading the volume from api
      if (this.player) {
        this.player.volume(vol / 100);
      }
    },
    timeSinceUpdate() {
  		const videoInstanceComponent = this;
		  this.timeDifferenceInterval = setInterval(() => {
	  		videoInstanceComponent.timeUpdateDifference += 1;
  		}, 1000);
  	},
    checkVideoDuration() {
      /**
       * Checks if the bulb duration can be updated from player duration getter.
       * Jwplayer: plays videos to trigger updateVideoDuration with accurate time
       * Videojs: waits for metadata to load duration
       */
      if (this.activeStream.player  === 'videojs') {
        const playerDuration = this.getPlayerDuration();
        if (playerDuration > 0) {
          if (playerDuration !== this.bulbVideo.video.playerDuration) {
            this.updateBulbVideoDuration({
              bulbVideoId:this.bulbVideo.id,
              duration: playerDuration,
            });
          }
          if (!this.bulbVideo.videoInitialized) {
            this.$set(this.bulbVideo, 'videoInitialized', true);
          }
        }
      }
    },
    async collectYoutubeCaptions() {
      const ytPlayer = _.get(this.player, 'tech_.player_.ytPlayer', null);
      if (
        this.ytHasCollectedYoutubeCaptions
        || this.ytCaptionCollectionAttempts >= 15
        || !ytPlayer
        || !this.bulbVideo.bulbVideoStreams.find(vs => vs.type === 'youtube')
        || this.bulbVideo.bulbVideoStreams.find(vs => vs.type === 'mp4')
      ) return;
      await this.$nextTick();
      ytPlayer.loadModule('captions');
      let ytCaptions = ytPlayer.getOption('captions', 'tracklist');
      if (ytCaptions && ytCaptions.length > (this.selectedVideo, 'captions.integration.length', 0)) {
        ytCaptions = ytCaptions.map(caption => {
          return {
            id: generateNumber('youtube-caption-id'),
            label: caption.displayName,
            language: caption.languageCode,
            type: 'youtube',
          };
        });
        this.bulbVideo.bulbVideoCaptions = this.bulbVideo.bulbVideoCaptions.concat(ytCaptions)
        this.ytHasCollectedYoutubeCaptions = true;
        if (!_.get(this.bulb, 'config.captions_disabled', false)) this.setCcOn({ bulb: this.bulb, bulbVideo: this.bulbVideo });
      } else {
        this.ytCaptionCollectionAttempts += 1;
      }
    },
    getYoutubeVideoID(urlOrVideoId) { // not actually being referenced here... remove!
      if (
        urlOrVideoId.length === 11
        && !urlOrVideoId.includes('youtube.com')
        && !urlOrVideoId.includes('.be')
      ) return urlOrVideoId;
      const regExp = /.(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#\&\?]).*/;
      const match = urlOrVideoId.match(regExp);
      if (match && match[1].length === 11) return match[1];
      throw new Error ('The sole argument for getYoutubeVideoID() must be a YouTube URL or a YouTube video ID.');
    },
    getPlayerTime(newDisplayTime) {
      /**
      * converts a display_time into player time
      * @param newDisplayTime: the display_time to convert
      */
      // [ ] move to vuex so question component in iframe can get
      const pt = newDisplayTime - this.bulbVideo.displayStart + this.displayTimeSkips(newDisplayTime);
      return pt;
    },
    getPlayerDuration() {
      /**
      * Returns the video duration using the correct video player API
      * if the player API or returns 0, null, or undefined return -1
      * 
      * [√] handle jankiness from certain player typesthat might return weird duration numbers.
      * So far these include:
      * > JWplayer
      * > Panopto
      *   > At this point in time, when this function is invoked, due to the initialization flow
      * that Panopto uses the video doesn't have a valid player / video duration. Rather, it has a player duration of NaN.
      * In order to obtain that necessary player duration from Panopto, we have to invoke the `loadVideo()` method from
      * the Panopto Embed API. Until we do that however, we have to use the default player duration on the video object.
      * > Vimeo
      *   > Specifically with the Password Protected Privacy settings
      *   > Haven't implemented this yet, however I know it's an issue because I ran into it a while ago and it made me ಥ﹏ಥ
      */
      if (this.useDefaultDuration) {
        return this.bulbVideo.video.playerDuration;
      }
      return this.player.duration() // || -1;
    },
    setMute(mute_on) {
      // [ ] read vuex volume before setting volume or muting
      if (this.player) {
        // volume method takes decimal from 0.0-1.0
        this.player.muted(mute_on);
      }
    },
    getPlayerState() {
      /**
      * Convert the current/active state of the player into the string 'paused' or 'play'
      * Used in comparison for syncState
      */
      if (this.player === 'videojs') {
        return this.player.paused() ? 'paused' : 'play';
      }
    },
    paused() {
      /**
      * Whether or not the player is paused
      */
      if (this.player) {
        return this.player.paused();
      }
    },
    resetYtCheck() {
      this.ytCheck = false;
      this.ytTick = false;
    },
    resetVimeoCheck() {
      this.vimeoCheck = false;
      this.vimeoTick = false;
    },
    resetPanoptoCheck() {
      this.panoptoCheck = false;
      this.panoptoTick = false;
    },
    requestSetDisplayTime(playerTime) {
      // sometimes playerTime jumps backwards in time, we were unable to determine why, this prevents the skip back from opening questions
      if (playerTime < this.playerTime && this.playerState === 'play') return;
      // console.log('\trequestSetDisplayTime', playerTime, 'delta:', Math.round(this.player.currentTime() - this.playerTime));
      this.playerTime = playerTime;
      if (this.currentSegment && this.initialized) {
        // YouTube needs a larger variance for time seeks (minimum 250 milliseconds)
        let jumpSeekVariance = 2;
        if (this.activeStream.type === 'video/youtube') jumpSeekVariance = 10000;
        // if the video was seeking to update_time and the difference between the new player time and the update time is small enough, turn off seeking
        // console.log(`updateTime: ${this.updateTime} playerTime: ${this.playerTime} seekVariance: ${this.seekVariance}`);
        if (this.seeking && this.updateTime >= 0 && Math.abs(this.updateTime - this.playerTime) <= this.seekVariance) {
          this.seeking = false
          this.seekTimestamp = 0;
        }
        else if (this.seeking && moment().valueOf() - this.seekTimestamp.valueOf() > (this.lookaround * jumpSeekVariance)) {
          /*
            I think the above should actually be: ((this.lookaround * jumpSeekVariance) * 1000) because we're currently trying to compare milliseconds with seconds.
          */
          // If seeing is stuck on for more than double the current lookaround - reset it and log error
          this.seeking = false
          this.seekTimestamp = 0;
          dataLayer.errorLog({
            status: 'player-stuck-seeking',
            message: `Player stuck in seeking. updateTime: ${this.updateTime} playerTime: ${this.playerTime} bulbId: ${this.bulb.id} userId: ${this.user.id} streamURL: ${this.activeStream.url} sessionId: ${this.sessionId}`,
            location: 'VideoInstance:requestSetDisplayTime',
          });
        }
        // display_time cannot update if any of the following is happening
        if (!this.seeking && !this.scrubbing && !this.pausing) {
          if (!this.seekOutOfCropSegment(this.playerTime)) {
            // update update time
            const t = Math.min(this.getDisplayTime(this.bulbVideo.id, playerTime), this.requiredMaxTime);
            // check for youtube mobile shenanigans, tried to break into function, didn't work
            if (this.ytSeekTime !== null && this.mobile && this.activeStream.type === 'video/youtube') {
              // prevent youtube from triggering a seek with a late timeupdate event
              if (playerTime - this.ytSeekTime < -.4 || playerTime - this.ytSeekTime > .3) this.ytSeekTime = null; // reset if outside of range
              if (this.ytSeekTime) return; // prevent update if youtube sendt time that is less than the last seek time
            }
            // console.log('\t\tsetDisplayTime', t.toFixed(3))
            this.setDisplayTime(t);
            // if display time was successfully set, the video is no longer buffering so trun it off.
            if (this.buffering) this.buffering = false
          }
        }
      }
    },
    seekOutOfCropSegment(playerTime) {
      /**
      * (HELPER METHOD)
      * gets passed a playerTime and checks to see if that time is in any cropped times (start_time or cropped sections)
      */
      const crop = this.intersectingCrop(playerTime, this.bulbVideo.allCrops, .2);
      if (crop && !this.seeking) {
        if (crop.start === this.bulbVideo.playerEndTime) {
          return false; // time is in the end crop time
        }
        EventBus.$emit('requestAction', {key: 'seek', seekPlayerTime: crop.end, video_id: this.bulbVideo.id});
        return true;
      }
      return false;
    },
    recenterCaptions(){
      /**
      * turn captions on and off again to re-center captions (might be an eaiser way to do this)
      */
      if (this.activeStream.player === 'videojs') {
        this.player.textTrackDisplay.updateDisplay();
      }
    },
    // updateVideoDuration(duration) {
    //   /**
    //   * This function checks the duration of the associated bulb and updates the data object
    //   * if there parameter is a different. The duration parameter should be a value passed in
    //   * from the jwplayer or other more exact durational time than the value stored in the
    //   * data object/database
    //   * @param duration: a new duration used to update the data object, taken from video player
    //   *                  property.
    //   */
    //   this.setBulb({
    //     duration,
    //     end_time: this.bulbVideo.userSetEndCrop ? Math.min(this.bulb.end_time, duration) : duration,
    //   })
    //   // recalculate crop segment times
    //   this.setBulb({ cropped_time: this.getCroppedTime() });
    // },
    syncState() {
      /**
      * Call when the current player state might be out of sync with the desired_player state
      * if the video should be playing and it isn't, play it
      * if the video should be paused but it isn't, pause it
      */
      // console.log('sync state',this.bulb.id, this.state, this.desiredState)
      if (this.currentSegment && this.desiredState !== this.getPlayerState()) {
        if (this.desiredState === 'play') {
          // console.log('here')
          EventBus.$emit('requestAction', 'play');
        } else {
          // console.log('pause due to no longer final segment')
          EventBus.$emit('requestAction', {key: 'pause', bypassGates: ['notInitialized', 'notCurrentSegment']});
        }
      }
    },
    triggerSyncState() {
      if (!this.scrubbing && this.activePausingInteractions.length === 0) {
        this.syncState();
      }
    },
    displayTimeSkips(time) {
      // time is a display_time
      /**
      * (HELPER METHOD) - called from getPlayerTime()
      * used when converting display_time to player_time
      * uses the calculated display_time in index 2 of skip_segments to add the correct
      * offset for the conversion process.
      * @param time: a display_time
      */
      let offset = 0;
      this.bulbVideo.allCrops.forEach((skip) => {
        if (this.getDisplayTime(this.bulbVideo.id, skip.end) <= time) {
          offset += skip.end - skip.start;
        }
      });
      return offset;
    },
    initializeStream() {
      /**
       * Load a new player based on the current_active stream data
       */
      this.streamInitialized = true;
      this.$nextTick(() => { 
        this.loadVideoJS();
        this.setupGateSettings(this.activeStream.player);
        this.startTimeUpdateLoop();
       });
    },
    destroyStream(playerType) {
      /**
       * Reset bulb source of truth data and player object for a new player stream
       * playerType: (string) type of the exisiting player (videojs, jwplayer, youtube, etc.)
       */
      // reset initialize variables/props that were set during initializeStream at the bulb level
      this.$set(this.bulbVideo, 'deviceInitialized', false);
      this.$set(this.bulbVideo, 'videoInitialized', false);
      if (this.isLiveSession) {
        this.setSidebarOpen(false);
        this.setSessionClicked(false);
      }
      this.setDisplayTimeStart(this.displayTime);
      // reset/destroy the player object
      this.destroyPlayer(playerType);
      // console.log('DESTROYING');
      this.stopTimeUpdateLoop();
    },
    destroyPlayer(type){
      /**
       * An abstracted method to destory the player.
       * Calls the corrosponding dispose/delete/remove for the give player type
       */
      this.disablePlayerHotkeysListeners();
      if (type === 'videojs') {
        // remove all listeners
        this.player.off();
        // destroy the player
        this.player.dispose();
        this.streamInitialized = false; // this makes vue remove the player from the DOM so when we go to reinitialize later our video tag will be there again
        this.player = null;
      }
    },
    loadVideoJS() {
      this.player_fuzz_time = .25;
      this.player = videojs(`video-embed-${this.bulbVideo.id}`, {
        captions: false,
        controls: false,
        controlBar:false,
        posterImage: false,
        textTrackSettings: false,
        autoplay: false,
        muted: true,
        preload: 'auto',
        techOrder: this.initializePlayerTechOrder(),
        playsinline: true,
        Panopto: {
          ...this.initializePlayerPanoptoPlaybackTechConfigurationOptions.bind(this)(),
        },
        Vimeo: {
          ...this.initializePlayerVimeoPlaybackTechConfigurationOptions.bind(this)(),
        },
        youtube: {
          ...this.initializePlayerYouTubePlaybackTechConfigurationOptions.bind(this)(),
        },
        html5: {
          ...this.initializePlayerHtml5PlaybackTechConfigurationOptions.bind(this)(),
        },
        Iframe: {
          ...this.initializePlayerIframePlaybackTechConfigurationOptions.bind(this)(),
        },
      });

      this.player.ready(() => {
        if (this.activeStream.type === 'video/youtube') {
          this.checkYoutubeBlocked()
          .then(blocked => {
            if (blocked) {
              if (!this.bulb.teachers[0].school || !this.bulb.teachers[0].school.proxy) {
                dataLayer.errorLog({
                  status: 'yt-blocked-proxy-disabled',
                  message: `Youtube is blocked, teacher does not have proxy ${JSON.stringify(this.bulb.teachers)}, sessionId: ${this.sessionId}`,
                  location: 'VideoInstance:checkYoutubeBlocked.then'
                });
                // no school or proxy disabled
                this.setSnackbar({
                  color: 'warning',
                  text: 'Youtube content may be blocked.',
                  timeout: 0,
                  action: 'youTubeBlocked',
                });
              } else if (this.bulbVideo.bulbVideoStreams.length < 2) {
                // school and proxy but no secondary stream
                dataLayer.errorLog({
                  status: 'yt-blocked-proxy-missing',
                  message: `Youtube is blocked and we are missing the proxy url ${JSON.stringify(this.bulbVideo)}, sessionId: ${this.sessionId}`,
                  location: 'VideoInstance:checkYoutubeBlocked.then'
                });
                this.setSnackbar({
                  color: 'error',
                  text: 'Content Blocked',
                  timeout: 0,
                  bluescreen: {
                    title: 'Content Blocked',
                    subtitle: 'Unable to load content',
                    text: `It appears your network is blocking content required for this ${this.termForBulb}. Please contact help@playposit.org.`,
                    location: `VideoInstance ${this.bulbVideo.id}`,
                  }
                });
              } else {
                // add log here that the player
                this.fallbackStream('youtube'); // enable logger
              }
            }
          })
          .catch(err => {
            PlayerService.apiLogger(`youtube CheckBlocked Error ${err.toString()} bulb ${this.bulb.id} section ${this.bulbAttempt.sectionId} student ${this.bulbAttempt.studentId} school ${this.schoolId}` )
            dataLayer.errorLog({
              status: 'yt-blocked-err',
              message: `There was an error while checking if youtube was blocked blocked ${err.toString()} ${JSON.stringify(this.bulbVideo)}, sessionId: ${this.sessionId}`,
              location: 'VideoInstance:checkYoutubeBlocked.catch'
            });
            this.fallbackStream('youtube');
          })
        }
        if (this.hasPlayerKeyboardShortcutsFeature) {
          if (this.bulbVideo.order === 0) {
            this.player.hotkeys({
              videoInstanceVm: this,
              EventBus,
              screenfull: this.screenfull,
              captureDocumentHotkeys: true,
            });
          }
        }
        this.player.removeChild('loadingSpinner');
      });

      this.player.src({
        src: this.activeStream.url,
        type: this.activeStream.type,
      });

      if (this.hasPlayerKeyboardShortcutsFeature) this.enablePlayerHotkeysListeners();
      if (process.env.IS_JEST) this.player.VhsLogger = () => {};
      this.player.VhsLogger({
        enabled: this.hasPlayerVhsLogging,
        activeStream: this.activeStream,
        debugPrefix: 'VideoInstance.vue',
        videoInstanceVm: this,
        // Enables or disables the VhsLogger video.js plugin logger (inception?)
        vhsLoggerEnabled: this.vhsLoggerEnabled,
        // Enables or disables the more human-readable format of the Hls.js debug logs
        hlsLoggerEnabled: this.hlsLoggerEnabled,
        // Enables or disables VHS debuggin in sicko mode...
        vhsDebuggingOverload: this.vhsDebuggingOverload,
        // Enables or disables HLS debuggin in sicko mode... defaults to using the standard Hls.js debug config option for logs.
        hlsDebuggingOverload: this.hlsDebuggingOverload,
        thirdPartyCookiesEnabled: this.thirdPartyCookiesEnabled,
      });

      this.player.on('error', (e) => {
        let err = this.player.error();
        if (err) {
          /**
           * * The most important error that we check for in this event, is if the media asset is corrupted somehow.
           * * The error that we recieve is called: MEDIA_ERR_DECODE and the associated error code is: 3.
           *    * Description of error:
           *      * Despite having previously been determined to be usable, an error occurred while trying to decode the media resource, resulting in an error.
           *    * more information could be found here: https://developer.mozilla.org/en-US/docs/Web/API/MediaError/code
           * 
           * * If we get a code 3 error in the last 3 seconds - trigger seek to end - seek will NOT bypass required interactions.
           * * This logic is implemented for the following scenarios:
           *    * Panopto MP4s being corrupt on Chrome/Edge in the last .5 seconds
           *      * Even though we haven't seen this with Panopto in some time, due to the many changes Panopto have implemented.
           *    * Some HLS config files
           *      * Mainly have seen this for some Mediasite videos.
           */
          if (err.isProquestError) {
            this.$enablePlaypositErrorScreen({
              vertical: true,
              active_color: this.$vuetify.theme.primary,
              base_color: this.$vuetify.theme.secondary,
              inactive_color: this.$vuetify.theme.tertiary,
              title: 'Video Error',
              subtitle: err.message,
              code: '613',
              text: '',
              icon: 'error',
              buttons: [],
            });
          } else if (
            err.code === 3
            && ['video/panopto', 'application/x-mpegURL', 'application/dash+xml'].includes(this.activeStream.type)
            && (this.displayDuration - this.displayTime) < 3
            ) {
              const bulbDataStreamType = (this.activeStream.type === 'video/panopto') ? `Panopto` : `HLS`;
              err.bulbData = `${bulbDataStreamType} Code 3 - VideoInstance: ${JSON.stringify(this.activeStream)} displayTime ${this.displayTime} bulbVideoId ${this.bulbVideo.id}`;
              EventBus.$emit('requestAction',
                {
                  key: 'seek',
                  seekPlayerTime: this.displayDuration,
                  video_id: this.bulbVideo.id,
                  bypassGates: [
                    'cannotPassMaxTime',
                  ],
                },
              );
            } else if (
              this.activeStream.type === 'video/youtube'
              && err?.code === 1150
            ) {
              this.setSnackbar({
                action: 'stream_help',
                bluescreen: {
                  title: 'YouTube Video Error',
                  subtitle: `A YouTube video inside this ${this.termForBulb} segment failed to load.`,
                  code: '511',
                  text: 'This usually means that the YouTube video has been deleted or is unable to be embedded within other websites. Please verify that the YouTube video is still valid.',
                },
                color: 'error',
                text: 'A YouTube video error was encountered.',
                timeout: 0,
              });
            } else {
              err.bulbData = `${JSON.stringify(this.activeStream)} displayTime ${this.displayTime} bulbVideoId ${this.bulbVideo.id}`;
              this.fallbackStream();
          }
          err.location = window.location.origin;
          dataLayer.errorLog(err, {
            sessionId: this.sessionId,
            status_data_keys: ['bulbData']
          });
        }
      });

      this.player.on('waiting', (e) => {
        this.checkYouTubePlay();
        this.buffering = true; // turn on buffering progress bar above video instance
      });

      this.player.tech(true).on('retryplaylist', (e) => {
        /*
        * retryplaylist fires for HLS streams that fail to load - meaning we have a manifest
        * but the playlists inside fail to load/play.  We retry loading them 3 times then trigger
        * fallbackStream which will move to the next stream if available or bluescreen
        */
        if (this.retryPlaylist === 2) {
          this.fallbackStream();
        }
        this.retryPlaylist += 1;
      });

      this.player.on('loadedmetadata', () => {
        /**
        * Videojs loads the duration and other metadata before the video has to play
        * Once it's loaded, update the video duration
        */
        this.$set(this.bulbVideo, 'metadataInitialized', true) // toggle metadata_initialized at the bulb level so the initialize_play button can be shown
        this.checkVideoDuration();
        this.setSpeed(this.bulb.config.speed);
        this.setVolume(this.volume);
        this.reloadCaptions();
        if (this.activeStream.type === 'video/youtube') {
          document.querySelectorAll('iframe.vjs-tech').forEach(v => v.setAttribute('tabindex', '-1'));
        }
      });

      this.player.on('durationchange',  () => {
        this.checkVideoDuration();
      });

      this.player.on('timeupdate', () => {
        /*
          Proquest videos can contain start/end crops separate from our own. When a Proquest video with a start crop is initializing, it immediately seeks to the crop's starting time. This triggers a `timeupdate` event that does away with the loading screen with the big play button. This can lock users out of seeing the video since the video's wrapper element won't appear until that big play button has been clicked.
        */
        if (
          this.activeStream.type === 'video/proquest'
          && !this.playButtonClicked
        ) return;
        const ct = this.player.currentTime();
        // duct tape to trigger player.ended for HLS streams
        if (
          (this.getPlayerDuration() - ct < .1 && this.player.ended())
          // Sometimes an HLS stream ends outside the .001 threshold and doesn't trigger an ended event
          || this.getPlayerDuration() - ct < (_.min([(this.lookaround * 3) + .01, .1]))
          && ['application/x-mpegURL','application/dash+xml'].includes(this.activeStream.type)) {
          this.player.trigger('ended');
        }

        this.initializeYouTubeVr(ct);

        // Setting up the extra logic needed for embedded bulbs that have parent
        // iframes without the permision of autoplay set.
        if (
          this.currentSegment
          && this.ytIframe
          && !this.ytInitialized
          && this.ytCheck
          && ct !== this.playerTime
          && ct > 0
        ) {
          this.ytTick = true;
        }

        if (
          this.currentSegment
          && this.vimeoIframe
          && !this.vimeoInitialized
          && this.vimeoCheck
          && ct !== this.playerTime
          && ct > 0
        ) {
          this.vimeoTick = true;
        }

        if (
          this.currentSegment
          && this.isPanoptoEmbedIframe
          && !this.panoptoInitialized
          && this.panoptoCheck
          && ct !== this.playerTime
          && ct > 0
        ) {
          this.panoptoTick = true;
        }

        // Duct tape for youtube not responding to user clicks in chrome in embedded iframe.
        // If iframe owner adds allow="autoplay *;" none of this is necessary.
        if (
          (this.ytInitializing === this.bulbVideo.id)
          && !this.seeking
        ) {
          if (ct > 0) this.youtubeInitialized();
        }

        this.handleVimeoPlayerEmbeddedPlaybackEvents(ct);
        this.handleVimeoPasswordProtectedVideoPlaybackEvents(ct);
        this.handleVimeoPlayerZerothInteractionPlaybackEvents(ct);
        this.handlePanoptoPlayerEmbeddedEvents(ct);

        if (!this.scrubbing && this.getPlayerDuration() !== this.player.currentTime()) { // not scrubbing && not at end of the video
          if (this.seeking && this.updateTime >= 0 && Math.abs(this.updateTime - this.player.currentTime()) <= this.seekVariance) this.seeking = false
          if (this.desiredState === 'play') this.startTimeUpdateLoop()
        }
        if (!this.bulbVideo.deviceInitialized) {
          // once a video has played, future calls to play() will work without click event.
          this.$set(this.bulbVideo, 'deviceInitialized', true);
          this.checkVideoDuration()
        }
        if ((!this.currentSegment && this.bulbVideo.videoInitialized)) {
          this.player.pause();
        }
        this.collectYoutubeCaptions();
      });

      this.player.on('play', () => {
        this.checkYouTubePlay();
        // showHotspots is duct tape for YouTube videos that are embedded with hotspot interactions - Hotspots cover the aditional play button YT requires to inittialize
        if (!this.showHotspots) this.setShowHotspots(true);
        if (this.currentSegment) {
          if (this.playerState !== 'play') {
            // something triggered a play event outside of the gate system
            // double check that it's allowed
            const allowed = this.isActionAllowed({key: 'play', bypassGates: []});
            if (allowed.failureAction) {
              EventBus.$emit('requestAction', {key: 'pause'});
              if (this.displayTime > this.requiredMaxTime) EventBus.$emit('requestAction', {key: 'seek', seekTime: this.requiredMaxTime})
              allowed.failureAction({key: 'play', bypassGates: []});
            }
          }
          this.setPlayerState('play');
        }
        this.buffering = false;
      });

      this.player.on('pause', (event) => {
        this.pausing = false;
        this.buffering = false;
        if (this.initializingBulb && !this.initialized) {
          this.setInitializingBulb(false);
        }
        if (this.currentSegment) {
          this.setPlayerState('paused');
        }
        // console.log('on pause', this.displayTime, this.getDisplayTime(this.bulbVideo.id, this.player.currentTime()), this.getPlayerTime(this.displayTime))
        // verify to prevent infinite loop
        if (Math.abs(this.displayTime - this.getDisplayTime(this.bulbVideo.id, this.player.currentTime())) > .026) {
          // player took too long to pause, seek back to when it should actually have paused.
          EventBus.$emit('requestAction', {key: 'seek', seekPlayerTime: this.getPlayerTime(this.displayTime), video_id: this.bulbVideo.id, preventSyncState: true});
        }
      });

      this.player.on('seeked', (event) => {
        this.resetYtCheck()
        this.resetVimeoCheck();
        this.resetPanoptoCheck();
        this.playerTime = this.player.currentTime();
        this.buffering = false; // turn off buffering progress bar above video instance
        if (this.currentSegment) {
          this.setPlayerState(this.paused() ? 'paused' : 'play');
          this.setCurrentSegmentSeek(true);
          if (this.mobile && this.activeStream.type === 'video/youtube') {
            // reset/update youtube seek time for mobile devices
            const pt = this.player.currentTime()
            if (!this.ytSeekTime || (pt - this.ytSeekTime < -.4 || pt - this.ytSeekTime > .3)) this.ytSeekTime = pt;
          }
          // console.log('startTimeUpdateLoop seeked')
          this.pausing = false;
          this.startTimeUpdateLoop()
        }
      });

      this.player.on('seeking', () => {
        this.playerTime = this.player.currentTime();
        this.buffering = true; // turn on buffering progress bar above video instance
      });
      
      this.player.on('ended', (event) => {
        if (this.mobileIOS && this.activeStream.type === 'video/panopto') this.setShowedMobileIOSPanoptoInitializeToast(false);
        // ensure that if the player ends but the
        // console.log('\tvideo ended ct:', this.currentSegment,this.displayTime, this.bulbVideo.displayEnd)
        if (this.currentSegment && this.displayTime < this.bulbVideo.displayEnd) {
          if (this.bulb.bulbVideos[this.index + 1]) {
            // console.log('\tnudge a', this.bulb.bulbVideos[this.index + 1].displayStart)
            this.setPlayerState('play')
            this.setDisplayTime(this.bulb.bulbVideos[this.index + 1].displayStart);
          }
          else {
            // console.log('nudge b', this.bulbVideo.displayEnd)
            this.setDisplayTime(this.bulbVideo.displayEnd);
          }
        }
      });

      this.player.on('texttrackchange', () => {
        if (!this?.player?.tech_?.childTextTracks?.()) return;
        const childTextTracks = this?.player?.tech_?.childTextTracks?.()?.map((textTrack) => ({
          ...textTrack,
          bulbVideoId: this.bulbVideo.id,
        }));
        if (childTextTracks.length === this.bulbVideo.bulbVideoCaptions.length) return;
        if (!this.bulbVideo.bulbVideoCaptions.length) {
          this.bulbVideo.bulbVideoCaptions.push(...childTextTracks);
        } else {
          for (const index in this.bulbVideo.bulbVideoCaptions) {
            const integrationCaption = this.bulbVideo.bulbVideoCaptions[index];
            const match = _.find(this.bulbVideo.bulbVideoCaptions, { id: integrationCaption.id });
            if (match) {
              this.$set(this.bulbVideo.bulbVideoCaptions, index, integrationCaption);
            } else {
              this.bulbVideo.bulbVideoCaptions.push(integrationCaption);
            }
          }
        }
      });

      const handles = document.getElementsByClassName('noUi-origin');
      if (handles[0]) handles[0].setAttribute('disabled', true); // disable hidden handle
      if (handles[2]) handles[2].setAttribute('disabled', true); // disable hidden max_required_time handle
    },
    handlePanoptoLoginShown(iframeSrc) {
      EventBus.$emit('panoptoLoginShown', iframeSrc);
    },
    setupPanoptoEvents() {
      window.addEventListener('panoptoInitIssueToast', this.handlePanoptoVideoInitIssue);
    },
    setupVimeoEvents() {
      window.addEventListener('vimeo-password-entered', this.handleVimeoPasswordEntered.bind(this));
    },
    destroyPanoptoEvents() {
      window.removeEventListener('panoptoInitIssueToast', this.handlePanoptoVideoInitIssue);
    },
    destroyVimeoEvents() {
      window.removeEventListener('vimeo-password-entered', this.handleVimeoPasswordEntered.bind(this));
    },
    handlePanoptoVideoInitIssue() {
      this.setPanoptoEmbedInitializationError(true);
      this.setSnackbar({
        color: 'warning',
        text: 'Error initializing the Panopto video.  If required, sign in, then refresh this web page before playing the video.',
        timeout: 0,
      });
    },
    handleVimeoPasswordEntered() {
      let alreadyEnteredVimeoVideoPassword = false;
      if (this.thirdPartyCookiesEnabled) {
        alreadyEnteredVimeoVideoPassword = sessionStorage.getItem('successfullyEnteredVimeoPassword') === 'true';
      }
      if (!alreadyEnteredVimeoVideoPassword) {
        this.fireToast({
          icon: 'success',
          iconColor: this.$vuetify.theme.primary,
          title: 'Successfully entered password for Vimeo video. This will last for your whole session, once the tab is closed, you will have to enter it again.',
          timer: 7000,
          customClass: {
            title: 'playposit-player-toast'
          },
          willClose: () => {
            if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('successfullyEnteredVimeoPassword', true);
          }
        });
      }
      setTimeout( () => {
        this.gateSettings.play();
      }, (!alreadyEnteredVimeoVideoPassword) ? 7000 : 0);
    },
    startTimeUpdateLoop() {
      if (!this.isTimeLoopActive && this.currentSegment) {
        // console.log('###Starting!', this.index, this.currentSegment);
        this.animationFrameId = requestAnimationFrame(this.throttledTimeUpdateLoop);
        // console.log('ID', this.animationFrameId);
        if (this.isLiveSession) this.timeSinceUpdate();
      }
    },
    timeUpdateLoop() {
      this.timeUpdateDifference = 0;
      this.isTimeLoopActive = true;
      if (window.ppDebug) console.log('animation', this.player.currentTime().toFixed(3));
      this.dynamicBuffer(this.player.currentTime().toFixed(3));
      if (this.player.paused()) {
        return this.stopTimeUpdateLoop();
      }
      if (this.bufferTimeUpdateLoop()) {
        return this.animationFrameId = requestAnimationFrame(this.throttledTimeUpdateLoop);
      }
      this.requestSetDisplayTime(this.player.currentTime());
      this.animationFrameId = requestAnimationFrame(this.throttledTimeUpdateLoop);
    },
    dynamicBuffer(nt) {
      let diff = Math.abs(this.currentAnimationTime - nt)
      if (window.ppDebug) console.log(`difference: ${diff}`);
      if ( 2 * diff < this.lookaround && diff > 0 ) { //check that the time progression step is twice faster than the Buffer and in fact progressing.
        this.bufferCounter++
        if (this.bufferCounter > 10) {
         	this.setLookaround(_.max([0.9 * this.lookaround, this.minimumLookAround])); //can place a safeguard here to minimize it to 100ms
         	this.bufferCounter = 0; //reset it
        }
      }
      this.currentAnimationTime = nt
    },
    stopTimeUpdateLoop() {
      // console.log('###Stopping!', this.index, this.player.currentTime());
      this.isTimeLoopActive = false;
      cancelAnimationFrame(this.animationFrameId);
      clearInterval(this.timeDifferenceInterval);
    },
    bufferTimeUpdateLoop() {
      /**
      * // DUCTTAPE YOUTUBE
      * tldr:
      * Seeking too early/when player time is 0 will trigger the youtube player back to "Unstarted" State,
      * therefore we need to wait at least one timeupdate event before triggering the requestSetDisplayTime
      *
      * Long Version:
      * Youtube (stupid as always) has issues with crop segments at the beginning of videos. It needs time to buffer and will reset to the "Unstarted" state if you try to seek too quickly which leads to errors when seeking out of crop segments that intersect the start time.
      * To help with the, bufferTimeUpdateLoop will prevent the first timeupdate from triggering requestAnimationFrame so that youtube has time to buffer
      * Because any of the player times before the crop start will trigger a seek, we don't need to seek on playerTime() === 0
      *
      * If there aren't crop segment at the beginning, we need to trigger a requestSetDisplayTime when playerTime() === 0
      * because otherwise the timing of youtube timeupdate events can delay the starting the animation frame loop until after .26 seconds have passed, missing the first cluster of questions.
      *
      * Therefore, if there are no crops within 1 second of the start of the video we want to trigger a requestSetDisplayTime
      */
      return  this.initialized &&                   // bulb is initialized
          (!this.player ||                          // player has been created
          this.player.currentTime() === 0) &&       // player time is 0
          this.activeStream.type === 'video/youtube' &&  // only use for youtube videos
          this.intersectingCrop(this.player.currentTime(), this.bulbVideo.allCrops, .2);
    },
    setupStream() {
      /*
        We need to preload mediaAmp videos so that videoJs can properly load them if there are any
      */
      // there should only be one mediaAmp stream per bulbVideo but the preloads all of them to be save.
      let mediaAmpStreams = _.filter(this.bulbVideo.bulbVideoStreams, {type: 'mediaamp'});
      if (mediaAmpStreams.length) {
        this.loadMediaAmpStreams(mediaAmpStreams)
        .then(data => { this.initializeStream(); })
        .catch(err => {
          dataLayer.errorLog(err);
          this.initializeStream();
          // this.fallbackStream();
        });
      }
      else {
        this.initializeStream();
      }
    },
    async initializeYouTubeVr(currentTime) {
      if (this.bulb?.teachers?.[0]?.school?.permissionKeys?.includes('enableYoutubeFriendlyUi')) return;
      // TODO TAM refactor: use videojs-vr instead of this duct tape.
      // this is duct tape logic to disable overlay for youtube
      if (this.isVr === null && this.activeStream.type === 'video/youtube') {
        const fov = await this.determineIfYouTubeVideoIsVr();
        if (fov) this.isVr = true;
        if (currentTime > 1 && !fov) this.isVr = false;
        if (this.isVr !== null) this.setYoutubeVr(this.isVr);
      }
    },
    async determineIfYouTubeVideoIsVr() {
      const isVr = await new Promise((resolve) => {
        let attempts = 0;
        const intervalId = setInterval(() => {
          attempts += 1;
          if (attempts >= 100) {
            clearInterval(intervalId);
            return resolve(false);
          }
          if (_.has(this?.player?.tech_?.ytPlayer?.getSphericalProperties?.(), 'fov')) {
            clearInterval(intervalId);
            return resolve(true);
          }
        }, 50);
      });
      return isVr;
    },
    enablePlayerHotkeysListeners() {
      this.disablePlayerHotkeysListeners();
      if (!_.isObject(this.playerHotkeysListeners) || !this.player) return;
      for (const key of Object.keys(this.playerHotkeysListeners)) {
        this.player.on(key, this.playerHotkeysListeners[key]);
      }
    },
    disablePlayerHotkeysListeners() {
      if (!_.isObject(this.playerHotkeysListeners) || !this.player) return;
      for (const key of Object.keys(this.playerHotkeysListeners)) {
        this.player.off(key, this.playerHotkeysListeners[key]);
      }
    },
    handleVimeoPasswordProtectedVideoPlaybackStart() {
      /**
       * This code block handles password protected Vimeo videos on mobile.
       */
      if (
        this.activeStream.type === 'video/vimeo'
        && this.activeStream.view === 4
      ) {
        this.$set(this.bulbVideo, 'deviceInitialized', true);
      }
    },
    handleVimeoPasswordProtectedVideoPlaybackEvents(ct) {
      if (
        this.activeStream.type === 'video/vimeo'
        && this.activeStream.view === 4
        && ct > 0 // this means that they have entered the password, and playback events are firing
        && (
          this.$parent.$refs.playerOverlayContainer.$el.style.pointerEvents
          || this.$parent.$refs.controlsWrapper.style.display === 'none'
        )
      ) {
        this.$parent.$refs.controlsWrapper.style.removeProperty('display');
        this.$parent.$refs.playerOverlayContainer.$el.style.removeProperty('pointer-events');
      }
    },
    handleVimeoPlayerZerothInteractionPlaybackStart() {
      if (
        this.activeStream.type === 'video/vimeo'
        && this?.interactions[0]?.displayInTime === 0
        && this.player.currentTime() <= 0
        && !this.vimeoIframe
      ) {
        this.setVolume(0);
        this.requestSetActiveInteraction(this?.interactions[0]);
      }
    },
    handleVimeoPlayerZerothInteractionPlaybackEvents(currentTime) {
      if (
        this.activeStream.type === 'video/vimeo'
        && this?.interactions[0]?.displayInTime === 0
        && (
          (currentTime > 0 && currentTime <= 0.5)
          || (currentTime > this.bulbVideo.playerStartTime && (currentTime < this.bulbVideo.playerStartTime + 1))
        )
      ) {
        this.setVolume(this.volume);
      }
    },
    /**
     * Duct tape for Vimeo videos not responding to user click in chrome within an embedded, parent iframe. For iFrame
     * delegation to work, the user in-control of the parent iframe needs to add the allow="autoplay" attribute in
     * order for playback to work. With that attribute added, none of this extra logic is needed.
     */
    handleVimeoPlayerEmbeddedPlaybackEvents(currentTime) {
      if (
        (this.vimeoInitializing === this.bulbVideo.id)
        && !this.seeking
      ) {
        if (currentTime > 0) {
          clearInterval(this.vimeoPlayPromiseInterval);
          this.VimeoInitialized();
        }
      }
    },
    /**
     * Duct tape for Panopto videos not responding to user click in Chrome within an embedded, parent iframe. For iFrame
     * delegation to work, the user in-control of the parent iframe needs to add the allow="autoplay" attribute in order for
     * playback to work. With that attribute added, none of this extra logic is needed. (╥﹏╥)
     */
    handlePanoptoPlayerEmbeddedEvents(currentTime) {
      if (
        (this.panoptoInitializing === this.bulbVideo.id)
        && !this.seeking
      ) {
        if (
          currentTime > 0
          || this?.interactions[0]?.displayInTime === 0 // edge case if there is an interaction at the zeroth timestamp
        ) {
          this.PanoptoInitialized();
          this.player.currentTime(this.bulbVideo.playerStartTime);
          this.startTimeUpdateLoop();
        }
      } else if (
        this.mobileIOS
        && (this.activeStream.type === 'video/panopto')
      ) {
        if (
          currentTime > 0
          && currentTime < 21
        ) {
          // iOS panopto video has successfully initialized and are receiving timeupdate events.
          this.PanoptoInitialized();
          this.startTimeUpdateLoop();
        }
      }
    },
    checkInternetConnectionBandwidth() {
      isFastNet((data) => {
        this.internetSpeed = data.isFast ? 'fast' : 'slow';
        this.internetLatencyData = data;
        this.handleInternetConnectionAlert();
      }, {
        timesToTest: 7,
        verbose: true,
      });
    },
    handleInternetConnectionAlert() {
      if (this.internetSpeed === 'slow') {
        this.setSnackbar({
          text: `Experiencing interruptions?
          This could impact video playback.
          `,
          action: 'internetSpeedTest',
          color: 'warning',
          timeout: 0,
        });
      }
    },
    checkIfAncestorOriginContainsCanvas() {
      return !!(_.find(this?.framedInExternalEnvironment?.ancestorOrigins, (origin) => {
          return (
            origin.includes('canvas')
            || origin.includes('instructure')
          )
        })
      );
    },
    checkIfAncestorOriginContainsD2l() {
      return !!(_.find(this?.framedInExternalEnvironment?.ancestorOrigins, (origin) => {
          return origin.includes('d2l')
        })
      );
    },
    togglePreloadingCaptions() {
      // Invoked only when preloaded captions are enabled, to 'refresh' the currently selected caption id
      // so the player renders them on the screen.
      if (this?.bulb?.config?.preloadCaptions) {
        this.toggleCC();
        setTimeout(() => {
          this.toggleCC();
        }, 700);
      }
    },
    setupRealtimeDebuggingOptions() {
      if (!this.thirdPartyCookiesEnabled) return;
      this.panoptoTechDebugOption = (sessionStorage.getItem('panoptoTechDebugOption') === 'true');
      this.vimeoTechDebugOption = (sessionStorage.getItem('vimeoTechDebugOption') === 'true');
      this.showHlsDebugginVideoOverlay = (sessionStorage.getItem('showHlsDebugginVideoOverlay') === 'true');
      this.showHlsDebugginStatsContainer = (sessionStorage.getItem('showHlsDebugginStatsContainer') === 'true');
      this.vhsLoggerEnabled = (sessionStorage.getItem('vhsLoggerEnabled') === 'true');
      this.hlsLoggerEnabled = (sessionStorage.getItem('hlsLoggerEnabled') === 'true');
      this.vhsDebuggingOverload = (sessionStorage.getItem('vhsDebuggingOverload') === 'true');
      this.hlsDebuggingOverload = (sessionStorage.getItem('hlsDebuggingOverload') === 'true');
    },
    /**
     * If the active stream is youtube use their api to enable captions.
     */
    handleSettingYouTubePlayerCaptions(id) {
      if (this?.activeStream?.type !== 'video/youtube') return;

      const caption = _.find(this?.bulbVideo?.bulbVideoCaptions, {id});
      if (_.get(caption, 'type', '') === 'youtube') {
        this.player.tech_.ytPlayer.setOption('captions', 'track', { languageCode: caption.language });
      }
      else {
        this.player.tech_.ytPlayer.setOption('captions', 'track', {});
      }
    },
    /**
     * If the active stream is vimeo && we are being blocked by the vimeo endpoint
     * use their embed player api to enable captions:
     */
    handleSettingVimeoPlayerCaptions(id) {
      if (this?.activeStream?.type !== PLAYER_VIDEO_INTEGRATION_TYPES.Vimeo) return;

      if (this.invokeVimeoFallbackCaptionLogic) {
        const caption = _.find(this.bulbVideo.bulbVideoCaptions, { id });
        if (_.get(caption, 'type', '') === 'vimeoEmbed') {
          (async () => {
            const {
              language,
              kind,
            } = caption;
            try {
              await this.player?.tech_?.vimeoPlayer?.enableTextTrack(language, kind);
            } catch (error) {
              switch (error.name) {
                case 'InvalidTrackLanguageError':
                this.setSnackbar({
                  color: 'warning',
                  text: 'There is no text track for the specified language.',
                  timeout: 5000,
                });
                break;
                case 'InvalidTrackError':
                this.setSnackbar({
                  color: 'warning',
                  text: 'There is no such text track to set.',
                  timeout: 5000,
                });
                break;
                default:
                this.setSnackbar({
                  color: 'warning',
                  text: 'Unknown Vimeo caption related error.',
                  timeout: 5000,
                });
                break;
              }
            }
          })();
        } else {
          (async () => {
            try {
              await this.player?.tech_?.vimeoPlayer?.disableTextTrack();
            } catch (error) {
              console.error(`Unable to disable text track for Vimeo embed video stream in bulb with id: ${this.bulb.id}`);
            }
          })();
        }
      }
    },
  },
  beforeDestroy() {
    this.destroyStream(this.activeStream.player);
    if (this.hasPanoptoVideoPlaybackFeature) this.destroyPanoptoEvents();
    this.destroyVimeoEvents();
    if (this.hasPlayerKeyboardShortcutsFeature) this.disablePlayerHotkeysListeners();
  },
  mounted() {
    this.setupRealtimeDebuggingOptions();
    this.videoInstanceEmbeddedInIframe;
    if (this.activeStream.type === 'video/panopto') this.checkInternetConnectionBandwidth();
    window.ppDebug = false;
    // Setting up namespaced debugging object for our VideoInstance Vue component
		window.PP.VideoInstance = {
			vm: this,
		};
    this.setupStream();
    this.setMinimumLookaround();
    if (this.hasPanoptoVideoPlaybackFeature) this.setupPanoptoEvents();
    this.setupVimeoEvents();
    // [ ] method for jumping to display time loaded externally,
    this.requestActionFunc = key => {
      let props = {};
      if (typeof key === 'string') {
        props.key = key;
      } else {
        props = key;
      }
      if (_.has(props, 'video_id') && props.video_id !== this.bulbVideo.id) return;
      if (!props.bypassGates) props.bypassGates = [];
      if (!this.requestAction[props.key]) return false;
      if (this.isOffline && props.key !== 'pause') return;
      this.requestAction[props.key](props);
    }
    EventBus.$on('transferActionRequest', obj => {
      if (!this.mobile) this.$set(this.bulbVideo, 'deviceInitialized', true);
      /*
      * With liveSessions we have to fire a pause event for YouTube, Vimeo, and Panopto because those players are embedded in an iFrame
      * and due to event delegations are not recognized causing the video to play in the background.  To prevent this from happening when a user
      * enters the waiting we pause the player once - then allow it to play normally.
      */
      if (
        ['video/youtube', 'video/panopto', 'video/vimeo'].includes(this.activeStream.type)
        && this.isLiveSession
        && !this.liveSessionCheck
      ) {
        this.liveSessionCheck = true;
        return this.gateSettings.pause();
      }
      /**
       * The code block below, mitigates the issue discovered on iOS where Panopto videos wouldn't play back.
       * This seemed to happen after Panopto pushed version 12.2 sometime late last week.
       */
      else if (
        this.mobileIOS
        && this.activeStream.type === 'video/panopto'
      ) {
        this.$set(this.bulbVideo, 'deviceInitialized', true);
        if (this.player.currentTime() === 0) {
          if (this.showedMobileIOSPanoptoInitializeToast) return;
          EventBus.$emit('disablePointerEventsForPanoptoOverlay');
          this.setSnackbar({
            text: `Please click play again.`,
            color: 'warning',
            timeout: 5000,
          });
          this.setShowedMobileIOSPanoptoInitializeToast(true);
          return;
        }
      }
      
      this.handleVimeoPasswordProtectedVideoPlaybackStart();
      this.handleVimeoPlayerZerothInteractionPlaybackStart();
      this.requestActionFunc(obj);
    });
    this.setInitialCaption();
    if (this.isLiveSession) {
      this.setupMediaSessionEvents();
      this.throttleTime = 200;
    }
  },
  watch: {
    'bulbVideo.bulbVideoCaptions': {
      handler(vjsLoad) {
        if (!this.bulb.hasCaptions) this.setBulbHasCaptions();
        vjsLoad.forEach((captionSet) => {
          if (captionSet.vjsLoad && !captionSet.vjsLoaded) {
            if (['youtube', 'vimeoEmbed'].includes(captionSet.type)) captionSet.vjsLoaded = true;
            else this.loadVtt(captionSet);
          }
        });
        this.setInitialCaption();
      },
      watch: 'deep',
    },
    'activeCaptions'() {
      this.setCaptions(this.bulbVideo.captionId);
    },
    'activeStream' (newStream, oldStream) {
      // destroy player associated with the old activeStream
      if (this.player) this.destroyStream(oldStream.player);
      this.$nextTick(() => {
        // reinit a new player using the new activeStream
        this.initializeStream();
      })
    },
    'bulbInitialized'() {
      this.streamFails = 0;
      if (this.bulbInitialized) {
        this.initializeHlsQuality();
        this.togglePreloadingCaptions();
      }
    },
    'bulbVideo.captionId'(n, o) {
      if (n && parseInt(n, 10) !== -1) this.selectedCaptionId = n;
    },
    ccOn(newValue, oldValue) {
      if (newValue) {
        this.bulbVideo.captionId = this.selectedCaptionId;
      } else {
        this.bulbVideo.captionId = -1;
      }
    },
    'captionReset'() {
      if (this.captionReset) this.recenterCaptions();
    },
    'currentSegment'(n, o) { // if not scrubbing, play!
      if (n) {
        if (!this.isTimeLoopActive) this.startTimeUpdateLoop(); //console.log('startTimeupdate loop, currentSegment');
        if (!this.manualPause && this.bulbVideo.order === 0) {
          EventBus.$emit('requestAction', {key:'play', bypassGates:['requiredQuestions']});
        }
        if (this.bulb?.teachers?.[0]?.school?.permissionKeys?.includes('enableYoutubeFriendlyUi')) {
          if (this.activeStream.type === 'video/youtube') this.setYoutubeVr(true);
          else this.setYoutubeVr(false);
        }
      }
      else {
        // console.log('not the current segement, stopping timeUpdatLop')
        this.stopTimeUpdateLoop();
      }
      
      if (this.initialized) {
        if (this.currentSegment) {
          // console.log(`${this.bulb.id} IS NOW THE CURRENT SEGMENT!`);
          // if (this.adaptive_bulb) this.triggerAdaptiveBulb();
          this.pausing = false;
          this.seeking = false;
          this.setMute(false);
          this.triggerSyncState();
          if (
            !this.bulb?.teachers?.[0]?.school?.permissionKeys?.includes('enableYoutubeFriendlyUi')
            && this.ytVr !== this.isVr
          ) {
            this.setYoutubeVr(this.isVr);
          }
          if (this.activeStream.type === 'video/youtube') {
            this.setYoutubeActiveStream(this.activeStream.url)
          }
          else if (this.youtubeActiveStream) {
            this.setYoutubeActiveStream(null)
          }
        } else {
          this.setMute(true);
          EventBus.$emit('requestAction', { key: 'pause', video_id: this.bulbVideo.id });
          this.$nextTick(() => {
            // DUCTTAPE video segment was seeking back before moving to the next one
            EventBus.$emit('requestAction', {
              key: 'seek', seekPlayerTime: this.bulbVideo.playerStartTime, video_id: this.bulbVideo.id, preventSyncState: true
            });
          })
        }
      }
    },
    async 'hideCaptionsEnableTranscript'(newValue) {
      /**
       * If captions are disabled when a learner toggles the hideCaptionsShowTranscript toggle we turn them on first and
       * then disable showing them in the player but continue to show them in the transcript.  I'm reusing the setCcOn
       * function as it handles not only setting the caption correctly, but also correctly keeping the on/off of the
       * captions and cc button in the scrubbar.
      */
      if (!this.activeCaptions) {
        await this.setCcOn({
          bulb: this.bulb,
          bulbVideo: this.bulbVideo,
          overwritePreloadCaptions: true, // used to force captions on to show in transcript
        });
      }
      if (newValue) {
        const tracks = this.player.textTracks();
        for (let t = 0; t < tracks.length; t += 1) {
          this.player.textTracks()[t].mode = 'disabled';
        }
      } else if (!newValue) this.setCaptions(this.bulbVideo.captionId);
    },
    'initialized'() {
      if (this.activeStream.type === 'video/youtube') {
        document.querySelectorAll('iframe.vjs-tech').forEach(v => v.setAttribute('tabindex', '-1'));
        if (this.hasNewInteractionDisplayAccess) this.setInteractionStates();
      }
    },
    'mute'() { this.setMute(this.mute)  },
    'selectedStreamQuality'(n, o) {
      if (n) this.setStreamQuality();
    },
    'speed'() { this.setSpeed(this.speed) },
    'seeking'() {
      if (!this.seeking) {
        this.updateTime = null;
      }
    },
    'scrubbing'() {
      this.triggerSyncState();
    },
    'timeUpdateDifference' (n, o) {
    	if (
    		this.isLiveSession
    		&& n
    		&& n > 1
  		) {
				this.timeUpdateLoop();
			}
    },
    'triggerFallbackStream'() {
      if (!this.bulbInitialized) {
        this.fallbackStream();
      }
    },
    'volume'() { this.setVolume(this.volume) },
    vimeoTechDebugOption(newValue, oldValue) {
      if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('vimeoTechDebugOption', newValue);
      this.fireToast({
        icon: 'success',
        iconColor: this.$vuetify.theme.primary,
        title: 'Applying Player debugging values...',
        timer: 1000,
        position: 'top-right',
        customClass: {
          title: 'playposit-player-toast'
        },
        willClose: () => {
          location.reload();
        }
      });
    },
    panoptoTechDebugOption(newValue, oldValue) {
      if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('panoptoTechDebugOption', newValue);
      this.fireToast({
        icon: 'success',
        iconColor: this.$vuetify.theme.primary,
        title: 'Applying Player debugging values...',
        timer: 1000,
        position: 'top-right',
        customClass: {
          title: 'playposit-player-toast'
        },
        willClose: () => {
          location.reload();
        }
      });
    },
    showHlsDebugginVideoOverlay(newValue, oldValue) {
      if (
        !this.hasPlayerVhsLogging
        || !this.hlsDebuggingOverload
      ) {
        const message = `You dont have access to this feature.\nIn order to access this -- you need the 'hasPlayerVhsLogging' feature flag and 'hlsDebuggingOverload' set to true.`;
        this.showHlsDebugginVideoOverlay = false;
        return console.log(`%c${message}`, CONSOLE_LOG_STYLE_TYPES.Warning.join(';'));
      }
      if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('showHlsDebugginVideoOverlay', newValue);
    },
    showHlsDebugginStatsContainer(newValue, oldValue) {
      if (
        !this.hasPlayerVhsLogging
        || !this.hlsDebuggingOverload
      ) {
        const message = `You dont have access to this feature.\nIn order to access this -- you need the 'hasPlayerVhsLogging' feature flag and 'hlsDebuggingOverload' set to true.`;
        this.showHlsDebugginStatsContainer = false;
        return console.log(`%c${message}`, CONSOLE_LOG_STYLE_TYPES.Warning.join(';'));
      }
      if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('showHlsDebugginStatsContainer', newValue);
    },
    vhsLoggerEnabled(newValue, oldValue) {
      if (!this.hasPlayerVhsLogging) {
        this.vhsLoggerEnabled = false;
        return console.log('%cYou dont have access to this feature.', CONSOLE_LOG_STYLE_TYPES.Warning.join(';'));
      }
      if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('vhsLoggerEnabled', newValue);
      this.fireToast({
        icon: 'success',
        iconColor: this.$vuetify.theme.primary,
        title: 'Applying Player debugging values...',
        timer: 1000,
        position: 'top-right',
        customClass: {
          title: 'playposit-player-toast'
        },
        willClose: () => {
          location.reload();
        }
      });
    },
    hlsLoggerEnabled(newValue, oldValue) {
      if (!this.hasPlayerVhsLogging) {
        this.hlsLoggerEnabled = false;
        return console.log('%cYou dont have access to this feature.', CONSOLE_LOG_STYLE_TYPES.Warning.join(';'));
      }
      if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('hlsLoggerEnabled', newValue);
      this.fireToast({
        icon: 'success',
        iconColor: this.$vuetify.theme.primary,
        title: 'Applying Player debugging values...',
        timer: 1000,
        position: 'top-right',
        customClass: {
          title: 'playposit-player-toast'
        },
        willClose: () => {
          location.reload();
        }
      });
    },
    hlsDebuggingOverload(newValue, oldValue) {
      if (!this.hasPlayerVhsLogging) {
        this.hlsDebuggingOverload = false;
        return console.log('%cYou dont have access to this feature.', CONSOLE_LOG_STYLE_TYPES.Warning.join(';'));
      }
      if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('hlsDebuggingOverload', newValue);
      this.fireToast({
        icon: 'success',
        iconColor: this.$vuetify.theme.primary,
        title: 'Applying Player debugging values...',
        timer: 1000,
        position: 'top-right',
        customClass: {
          title: 'playposit-player-toast'
        },
        willClose: () => {
          location.reload();
        }
      });
    },
    vhsDebuggingOverload(newValue, oldValue) {
      if (!this.hasPlayerVhsLogging) {
        this.vhsDebuggingOverload = false;
        return console.log('%cYou dont have access to this feature.', CONSOLE_LOG_STYLE_TYPES.Warning.join(';'));
      }
      if (this.thirdPartyCookiesEnabled) sessionStorage.setItem('vhsDebuggingOverload', newValue);
      this.fireToast({
        icon: 'success',
        iconColor: this.$vuetify.theme.primary,
        title: 'Applying Player debugging values...',
        timer: 1000,
        position: 'top-right',
        customClass: {
          title: 'playposit-player-toast'
        },
        willClose: () => {
          location.reload();
        }
      });
    },
  },
};

</script>

<style lang="scss">
div[class^="vi-player-"] {
  padding-top: 56.25%;
  height: 0;
  position: relative;

  &.youtube-friendly-ui {
    padding-top: 46.25%;
  }
}

/*
  Maintain the player dimesions we set on our player (above) for responsive embedded videos using the Vimeo player
  sdk.

  We do this because responsive Vimeo videos will automatically adjust to accommodate a specific screen or window size.
  When using Vimeo's responsive embed code (which we do), the player will adjust to the dimensions of the container where it's placed,
  (which is defined above) all while preserving the video's original aspect ratio.
  
  In our case, there are some older Vimeo videos in bulbs that have smaller aspect ratios (240 / 320) which when enabled
  with Vimeos' responsive code, in conjunction with the padding set above, causes those videos to 'slide-off' the
  player container which results in removing some aspects of the video.
*/
div[id*="_vimeo_api"] > div {
  padding-top: 56.25% !important;
}


div[id^="video-embed-"]{  
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  width: 100% !important;
  height: 100% !important;
}

video.vjs-tech, div[id^="video-embed-"] > div{
  width: 100%;
  height: 100%;
}

.landscape .vjs-text-track-display > div:first-child,
.desktop .vjs-text-track-display > div:first-child {
  margin-bottom: 62px !important;
}

.buffer-progress {
  height: 3px !important;
  margin: 0 !important;
  position: absolute;
  top: 0;
  z-index: 1;
}

.player-offline-buffer {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  color: var(--branding-active-hex);
}

/* phones */
@media screen and (min-width: 0px) and (max-width: 480px) {
  .player-offline-buffer {
    top: 13%;
  }
}

/* tablets */
@media screen and (min-width: 768px) and (max-width: 1024px) {
  .player-offline-buffer {
    top: 19%;
  }
}


.vjs-text-track-display > div{
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.vjs-text-track-cue {
  position: relative !important;
  bottom: 0 !important;
  top: 0 !important;
}

#transparentYouTubeBlocker {
	height: 50px;
	width: 100%;
	background: transparent;
	position: absolute;
	right: 0;
	bottom: 0;
	z-index: 3;
}

/* Styling associated with the crop start time work around fix */
.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
	transition-delay: 1s;
}
.fade-enter, .fade-leave-to, .vimeo-zeroth-interaction-fade-enter, .vimeo-zeroth-interaction-fade-leave-to {
  opacity: 0;
}

/*
Styling associated specifically with Vimeo videos that use the Vimeo player SDK and have interactions at the zeroth
timestamp
*/
.vimeo-zeroth-interaction-fade-enter-active, .vimeo-zeroth-interaction-fade-leave-active {
  transition: opacity 1.5s;
	transition-delay: 1s;
}

.hls-debugging-overkill-video-class {
  position: absolute;
  top: 0;
  right: 0;
  width: 500px;
  height: 300px;
}

.hls-debugging-overkill-stats-container {
  position: absolute;
  top: 0;
  left: 0;
  background-color: gray;
  border-radius: 10px;
}

.hls-debugging-overkill-stats-container > pre {
  border-color: #333;
  color: #ccc;
  display: block;
  padding: 9.5px;
  margin: 0 0 10px;
  font-size: 13px;
  line-height: 1.42857143;
  color: #333;
  word-break: break-all;
  word-wrap: break-word;
  background-color: #f5f5f5;
  border: 1px solid #ccc;
  border-radius: 4px;
}

</style>